import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import styled, { keyframes, css } from "styled-components";
import mammoth from "mammoth";
import theme from "../../../../assets/theme";
import CaseService from "../../../../services/CaseService";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import BundleService from "../../../../services/BundleService";
import {
  ModalBackdrop,
  ModalCard,
  ModalAccent,
  ModalBody,
  ModalHeader,
  ModalHeaderContent,
  ModalCloseBtn,
  ModalTitle,
  ModalSubtitle,
  ModalFooter,
  ModalCancelBtn,
  FieldGroup,
  FieldInput,
  IcoX,
  G,
} from "./styles";
import { Document as PdfDocument, Page as PdfPage } from "react-pdf";
import * as PDFJS from "pdfjs-dist/legacy/build/pdf";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";

PDFJS.GlobalWorkerOptions.isEvalSupported = false;
PDFJS.GlobalWorkerOptions.workerSrc = workerSrc;

const HIGHLIGHT_COLORS = [
  {
    key: "yellow",
    label: "Yellow",
    bg: "#F59E0B",
    alpha: "rgba(245,158,11,0.28)",
  },
  {
    key: "green",
    label: "Green",
    bg: "#10B981",
    alpha: "rgba(16,185,129,0.28)",
  },
  { key: "blue", label: "Blue", bg: "#3B82F6", alpha: "rgba(59,130,246,0.25)" },
  { key: "pink", label: "Pink", bg: "#EC4899", alpha: "rgba(236,72,153,0.25)" },
  {
    key: "orange",
    label: "Orange",
    bg: "#F97316",
    alpha: "rgba(249,115,22,0.28)",
  },
];

const PAGE_GAP = 22;
const RENDER_BUFFER = 2;
// Keeps the reading position while users move between already-open document tabs.
// This intentionally lives outside the component so changing the active tab does
// not discard the previous document's position.
const documentPagePositions = new Map();

const getDocumentPagePositionKey = (doc = {}) =>
  String(doc.id || doc.sourceDocId || doc.s3url || doc.name || "");

const getExhibitId = (doc) => {
  if (!doc) return null;
  return (
    doc.exhibitId ||
    doc.exhibit_id ||
    doc.exhibitCode ||
    doc.exhibit_code ||
    doc.properties?.exhibitId ||
    doc.properties?.exhibit_id ||
    doc.properties?.exhibitNo ||
    doc.properties?.exhibit_no ||
    null
  );
};

const ZOOM_PRESETS = [30, 40, 50, 75, 100, 125, 150, 175, 200];

let _nextId = 1;
const uid = () => `h${_nextId++}`;

function detectFileType(url = "") {
  const c = url.split("?")[0].toLowerCase();
  if (c.endsWith(".pdf")) return "pdf";
  if (c.endsWith(".docx")) return "docx";
  if (c.endsWith(".doc")) return "doc";
  if (/\.(jpe?g|png|gif|webp|svg|bmp)$/.test(c)) return "image";
  if (c.endsWith(".txt")) return "txt";
  if (/\.(xlsx|xls|pptx|ppt)$/.test(c)) return "office";
  if (/\.(mp4|mkv|mov|webm)$/.test(c)) return "video";
  if (/\.(mp3|wav|ogg|m4a)$/.test(c)) return "audio";
  return "unknown";
}

async function fetchDocxHtml(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer: buf });
  return result.value;
}

const storageKey = (id) => `doc_annotations_${id}`;
const notesKey = (id) => `doc_notes_${id}`;

function loadAnnotations(docId) {
  try {
    return JSON.parse(localStorage.getItem(storageKey(docId)) || "[]");
  } catch {
    return [];
  }
}
function saveAnnotations(docId, v) {
  try {
    localStorage.setItem(storageKey(docId), JSON.stringify(v));
  } catch {}
}
function loadNotes(docId) {
  try {
    return JSON.parse(localStorage.getItem(notesKey(docId)) || "[]");
  } catch {
    return [];
  }
}
function saveNotes(docId, v) {
  try {
    localStorage.setItem(notesKey(docId), JSON.stringify(v));
  } catch {}
}

// Rects are stored relative to the PAGE CANVAS so overlays rendered
// inside each page's local coordinate system line up correctly.
function getRectsRelativeToWrap(range, _pdfWrapEl, canvasEl) {
  // Use the canvas element of the specific page as the coordinate origin.
  // If we can't find it, fall back to the selection's bounding rect origin.
  const originEl = canvasEl || _pdfWrapEl?.querySelector("canvas");
  const originRect = originEl
    ? originEl.getBoundingClientRect()
    : range.getBoundingClientRect();

  const rects = Array.from(range.getClientRects())
    .filter((r) => r.width > 0 && r.height > 0)
    .map((r) => ({
      left: r.left - originRect.left,
      top: r.top - originRect.top,
      width: r.width,
      height: r.height,
    }));

  // canvasOffset is now always {0,0} because rects are already canvas-relative
  return { rects, canvasOffset: { left: 0, top: 0 } };
}

function getViewportAnchorFromRange(range) {
  const rects = Array.from(range.getClientRects()).filter(
    (r) => r.width > 0 && r.height > 0,
  );
  if (!rects.length) {
    const br = range.getBoundingClientRect();
    return {
      x: br.left + br.width / 2,
      y: br.bottom,
      width: br.width,
      height: br.height,
    };
  }
  const last = rects[rects.length - 1];
  return {
    x: last.left + last.width / 2,
    y: last.bottom,
    width: last.width,
    height: last.height,
  };
}

async function downloadPdfWithAnnotations(url, annotations, notes, docName) {
  let jsPDF = window.jspdf?.jsPDF;
  if (!jsPDF) {
    await new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src =
        "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = res;
      s.onerror = rej;
      document.head.appendChild(s);
    });
    jsPDF = window.jspdf.jsPDF;
  }

  const RENDER_SCALE = 2.5;
  const loadingTask = PDFJS.getDocument(url);
  const pdfDoc = await loadingTask.promise;
  const numPages = pdfDoc.numPages;

  const firstPage = await pdfDoc.getPage(1);
  const vp0 = firstPage.getViewport({ scale: RENDER_SCALE });

  const pdf = new jsPDF({
    orientation: vp0.width > vp0.height ? "l" : "p",
    unit: "px",
    format: [vp0.width, vp0.height],
    compress: true,
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: RENDER_SCALE });
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: ctx, viewport }).promise;

    const pageAnns = annotations.filter((a) => a.page === pageNum);
    for (const ann of pageAnns) {
      if (!ann.rects?.length) continue;
      const color =
        HIGHLIGHT_COLORS.find((c) => c.key === ann.color) ||
        HIGHLIGHT_COLORS[0];
      const factor = RENDER_SCALE / (ann.cssScale || 1);
      const co = ann.canvasOffset || { left: 0, top: 0 };
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = color.bg;
      for (const r of ann.rects) {
        ctx.fillRect(
          (r.left - co.left) * factor,
          (r.top - co.top) * factor,
          r.width * factor,
          r.height * factor,
        );
      }
      ctx.restore();
    }

    const pageNotes = notes.filter((n) => n.page === pageNum && n.x != null);
    for (const n of pageNotes) {
      const factor = RENDER_SCALE / (n.cssScale || 1);
      const nx = n.x * factor,
        ny = n.y * factor;
      const W = 180 * RENDER_SCALE,
        H = 90 * RENDER_SCALE;
      ctx.save();
      ctx.globalAlpha = 0.95;
      ctx.fillStyle = "#FEF9C3";
      ctx.strokeStyle = "#F59E0B";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(nx, ny, W, H, 8);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#444";
      ctx.font = `bold ${11 * RENDER_SCALE}px sans-serif`;
      ctx.fillText("NOTE", nx + 12 * RENDER_SCALE, ny + 22 * RENDER_SCALE);
      ctx.font = `${10 * RENDER_SCALE}px sans-serif`;
      const words = n.text.split(" ");
      let line = "",
        lineY = ny + 42 * RENDER_SCALE;
      for (const word of words) {
        const test = line + word + " ";
        if (ctx.measureText(test).width > W - 24 * RENDER_SCALE && line) {
          ctx.fillText(line.trim(), nx + 12 * RENDER_SCALE, lineY);
          line = word + " ";
          lineY += 13 * RENDER_SCALE;
          if (lineY > ny + H - 15 * RENDER_SCALE) break;
        } else {
          line = test;
        }
      }
      if (line) ctx.fillText(line.trim(), nx + 12 * RENDER_SCALE, lineY);
      ctx.restore();
    }

    const pageAnnsWithComment = pageAnns.filter((a) => a.comment?.trim());
    for (const ann of pageAnnsWithComment) {
      if (!ann.rects?.length) continue;
      const factor = RENDER_SCALE / (ann.cssScale || 1);
      const co = ann.canvasOffset || { left: 0, top: 0 };
      const firstRect = ann.rects[0];
      const commentX = (firstRect.left - co.left) * factor + 6;
      const commentY = (firstRect.top - co.top) * factor - 18;
      ctx.save();
      ctx.fillStyle = "#1e293b";
      ctx.font = `bold ${10 * RENDER_SCALE}px sans-serif`;
      ctx.fillText(
        "💬 " + ann.comment,
        commentX,
        Math.max(25 * RENDER_SCALE, commentY),
      );
      ctx.restore();
    }

    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    if (pageNum === 1) {
      pdf.addImage(imgData, "JPEG", 0, 0, vp0.width, vp0.height);
    } else {
      pdf.addPage(
        [viewport.width, viewport.height],
        viewport.width > viewport.height ? "l" : "p",
      );
      pdf.addImage(imgData, "JPEG", 0, 0, viewport.width, viewport.height);
    }
  }

  pdf.save((docName || "document").replace(/\.[^.]+$/, "") + "-annotated.pdf");
}

function SelectionTip({ anchorPoint, text, onSave, onCancel }) {
  const [comment, setComment] = useState("");
  const [color, setColor] = useState("yellow");
  const tipRef = useRef(null);

  if (!anchorPoint) return null;

  const left = Math.max(
    12,
    Math.min(anchorPoint.left - 140, window.innerWidth - 290),
  );
  let top = anchorPoint.bottom + 12;
  if (top > window.innerHeight - 260) top = anchorPoint.top - 240;
  top = Math.max(12, top);

  return (
    <TipBox ref={tipRef} style={{ position: "fixed", top, left, zIndex: 9999 }}>
      <TipPreview>
        "{text.length > 68 ? text.slice(0, 68) + "…" : text}"
      </TipPreview>
      <TipLabel>Annotation Colour</TipLabel>
      <TipColorRow>
        {HIGHLIGHT_COLORS.map((c) => (
          <TipColorDot
            key={c.key}
            $bg={c.bg}
            $selected={color === c.key}
            onClick={() => setColor(c.key)}
            title={c.label}
          />
        ))}
      </TipColorRow>
      <TipInput
        placeholder="Add comment (optional)…"
        value={comment}
        autoFocus
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSave({ comment, color });
          }
        }}
      />
      <TipActions>
        <TipCancel onClick={onCancel}>Cancel</TipCancel>
        <TipSave onClick={() => onSave({ comment, color })}>
          Save Annotate
        </TipSave>
      </TipActions>
    </TipBox>
  );
}

// ─── NoteInputTip ─────────────────────────────────────────────────────────────

function NoteInputTip({ anchorPoint, page, onSave, onCancel }) {
  const [text, setText] = useState("");
  if (!anchorPoint) return null;

  return (
    <TipBox
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
        minWidth: 280,
      }}
    >
      <TipLabel>Note — Page {page ?? "Document"}</TipLabel>
      <NoteInputTA
        placeholder="Type your note here..."
        value={text}
        autoFocus
        rows={4}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.ctrlKey && e.key === "Enter" && text.trim()) onSave(text);
        }}
      />
      <div style={{ fontSize: 10, color: "#94A3B8", marginBottom: 10 }}>
        Ctrl + Enter to save
      </div>
      <TipActions>
        <TipCancel onClick={onCancel}>Cancel</TipCancel>
        <TipSave onClick={() => text.trim() && onSave(text)}>Add Note</TipSave>
      </TipActions>
    </TipBox>
  );
}

// ─── StickyNotePin ────────────────────────────────────────────────────────────

const StickyNotePin = React.memo(function StickyNotePin({
  note,
  zoom,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const scale = (zoom || 100) / 100;
  const factor = scale / (note.cssScale || 1);
  return (
    <div
      style={{
        position: "absolute",
        left: note.x * factor,
        top: note.y * factor,
        zIndex: 50,
        pointerEvents: "all",
      }}
    >
      <NotePin onClick={() => setOpen((o) => !o)} title="Click to view note" />
      {open && (
        <StickyCard>
          <StickyText>{note.text}</StickyText>
          <StickyMeta>
            Page {note.page ?? "DOCX"} {note.time && `· ${note.time}`}
          </StickyMeta>
          <StickyDelete onClick={() => onDelete(note.id)}>
            Delete note
          </StickyDelete>
        </StickyCard>
      )}
    </div>
  );
});

// ─── PdfSkeleton (placeholder for unrendered pages) ───────────────────────────

function PdfPagePlaceholder({ width, height }) {
  return (
    <PlaceholderWrap style={{ width, height }}>
      <SkeletonShimmer />
      <PlaceholderInner>
        <SkeletonBar style={{ width: "55%", height: 14, marginBottom: 20 }} />
        <SkeletonBar style={{ width: "100%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "92%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "85%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "78%", height: 9, marginBottom: 24 }} />
        <SkeletonBar style={{ width: "100%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "95%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "60%", height: 9, marginBottom: 24 }} />
        <SkeletonBar style={{ width: "100%", height: 90, marginBottom: 20 }} />
        <SkeletonBar style={{ width: "90%", height: 9, marginBottom: 7 }} />
        <SkeletonBar style={{ width: "70%", height: 9 }} />
      </PlaceholderInner>
    </PlaceholderWrap>
  );
}

function buildPageLayout(pageSizes, zoom) {
  const scale = zoom / 100;
  let top = 0;
  let maxWidth = 0;
  const pages = pageSizes.map((size, index) => {
    const width = Math.max(1, Math.round(size.width * scale));
    const height = Math.max(1, Math.round(size.height * scale));
    const page = { pageNumber: index + 1, top, width, height };
    top += height + PAGE_GAP;
    maxWidth = Math.max(maxWidth, width);
    return page;
  });
  return {
    pages,
    width: maxWidth,
    height: Math.max(0, top - PAGE_GAP),
  };
}

function findPageIndexAtOffset(pages, offset) {
  if (!pages.length) return 0;
  let low = 0;
  let high = pages.length - 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const page = pages[mid];
    if (offset < page.top) high = mid - 1;
    else if (offset >= page.top + page.height + PAGE_GAP) low = mid + 1;
    else return mid;
  }
  return Math.max(0, Math.min(pages.length - 1, low));
}

// ─── VirtualPdfPage ─────────────────────────────────────────────────────────
// Thin wrapper around react-pdf's <Page>. react-pdf handles canvas render,
// text layer, and cancellation internally — we just need our own
// loading/error/annotation overlay chrome around it, same look as before.
const VirtualPdfPage = React.memo(function VirtualPdfPage({
  pageNumber,
  zoom,
  width,
  height,
  annotations,
  notes,
  onDeleteAnnot,
  onDeleteNote,
}) {
  const [rendered, setRendered] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const wrapperRef = useRef(null);

  // Reset the "rendered" flag whenever the page identity or retry changes
  // so the placeholder shows again while react-pdf re-renders the page.
  useEffect(() => {
    setRendered(false);
    setRenderError(null);
  }, [pageNumber, renderAttempt]);

  const pageAnnotations = annotations.filter(
    (a) => a.page === pageNumber && a.rects?.length > 0,
  );
  const pageNotes = notes.filter((n) => n.x != null && n.page === pageNumber);

  return (
    <PdfPageContainer
      data-pdf-page={pageNumber}
      ref={wrapperRef}
      style={{ margin: 0 }}
    >
      <div
        style={{
          position: "relative",
          width,
          height,
          minWidth: width,
          minHeight: height,
        }}
      >
        {!rendered && <PdfPagePlaceholder width={width} height={height} />}
        {renderError && (
          <div
            role="alert"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              zIndex: 10,
            }}
          >
            <strong
              style={{ display: "block", fontSize: 13, color: "#dc2626" }}
            >
              Page {pageNumber} did not load
            </strong>
            <span style={{ fontSize: 11, color: "#64748b" }}>
              {renderError}
            </span>
            <button
              onClick={() => setRenderAttempt((value) => value + 1)}
              style={{
                display: "block",
                margin: "10px auto 0",
                padding: "4px 10px",
                fontSize: 11,
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        )}

        <div style={{ display: rendered ? "block" : "none" }}>
          <PdfPage
            key={renderAttempt}
            pageNumber={pageNumber}
            width={width}
            renderAnnotationLayer={false}
            renderTextLayer={true}
            loading={null}
            onRenderSuccess={() => setRendered(true)}
            onRenderError={(err) =>
              setRenderError(err?.message || "This page could not be rendered.")
            }
          />
        </div>

        {/* Annotations overlay */}
        {rendered &&
          pageAnnotations.map((ann) => (
            <PdfAnnotOverlay
              key={ann.id}
              ann={ann}
              zoom={zoom}
              color={
                HIGHLIGHT_COLORS.find((c) => c.key === ann.color) ||
                HIGHLIGHT_COLORS[0]
              }
              onDelete={onDeleteAnnot}
            />
          ))}

        {/* Sticky notes */}
        {rendered &&
          pageNotes.map((n) => (
            <div key={n.id} data-notepin="1">
              <StickyNotePin note={n} zoom={zoom} onDelete={onDeleteNote} />
            </div>
          ))}
      </div>
    </PdfPageContainer>
  );
});

function PdfViewer({
  url,
  currentPage,
  setCurrentPage,
  setNumPages,
  numPages,
  zoom,
  annotations,
  notes,
  onAddAnnot,
  onDeleteAnnot,
  onAddNote,
  onDeleteNote,
  noteMode,
  scrollContainerRef,
  scrollTargetPage,
  onScrollTargetConsumed,
  pageSize,
  setPageSize,
  isProgrammaticScroll,
  singlePageView = false,
}) {
  const [pending, setPending] = useState(null);
  const [notePending, setNotePending] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null); // react-pdf's PDFDocumentProxy (from onLoadSuccess)
  const [loadError, setLoadError] = useState(null);
  const [pageSizes, setPageSizes] = useState([]);
  const [renderRange, setRenderRange] = useState({ start: 1, end: 6 });
  const pdfWrapRef = useRef(null);
  const offsetTopRef = useRef(0);
  const rafRef = useRef(null);

  // react-pdf requires a stable `options` object reference, otherwise it
  // treats every render as "file changed" and reloads the doc.
  const documentOptions = useMemo(
    () => ({
      disableAutoFetch: true,
      disableStream: false,
      disableRange: false,
      rangeChunkSize: 262144,
    }),
    [],
  );

  // Reset local state whenever the doc url changes; <Document file={url}>
  // itself will trigger onLoadSuccess/onLoadError again on its own.
  useEffect(() => {
    setPdfDoc(null);
    setPageSizes([]);
    setLoadError(null);
    setRenderRange({ start: 1, end: 6 });
  }, [url]);

  const handleDocLoadSuccess = useCallback(
    (pdf) => {
      setLoadError(null);
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
    },
    [setNumPages],
  );

  const handleDocLoadError = useCallback((err) => {
    setLoadError(err?.message || "Could not load PDF.");
  }, []);

  // Measure/Probe page viewports dynamically (uses the same pdf proxy
  // react-pdf already loaded — no second fetch/parse of the file).
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    const measurePages = async () => {
      try {
        const firstPage = await pdfDoc.getPage(1);
        if (cancelled) return;
        const defaultViewport = firstPage.getViewport({ scale: 1 });
        const defaultSize = {
          width: defaultViewport.width,
          height: defaultViewport.height,
        };
        if (typeof setPageSize === "function") {
          setPageSize(defaultSize);
        }

        const sizes = new Array(pdfDoc.numPages).fill(defaultSize);
        setPageSizes(sizes);
      } catch (err) {
        if (!cancelled)
          setLoadError(err?.message || "Could not read PDF pages.");
      }
    };
    measurePages();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, setPageSize]);

  const pageLayout = useMemo(
    () => buildPageLayout(pageSizes, zoom),
    [pageSizes, zoom],
  );

  // Scroll-driven virtualization
  const computeRange = useCallback(() => {
    const container = scrollContainerRef?.current;
    const wrap = pdfWrapRef.current;
    if (!container || !wrap || !pageLayout.pages.length) return;

    if (singlePageView) {
      setRenderRange((prev) =>
        prev.start === currentPage && prev.end === currentPage
          ? prev
          : { start: currentPage, end: currentPage },
      );
      return;
    }

    const contRect = container.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const offsetTop = wrapRect.top - contRect.top + container.scrollTop;
    offsetTopRef.current = offsetTop;

    const relTop = Math.max(0, container.scrollTop - offsetTop);
    const relBottom = relTop + container.clientHeight;
    const firstVisible = findPageIndexAtOffset(pageLayout.pages, relTop);
    const lastVisible = findPageIndexAtOffset(pageLayout.pages, relBottom);
    const first = Math.max(1, firstVisible + 1 - RENDER_BUFFER);
    const last = Math.min(
      pageLayout.pages.length,
      lastVisible + 1 + RENDER_BUFFER,
    );

    setRenderRange((prev) =>
      prev.start === first && prev.end === last
        ? prev
        : { start: first, end: last },
    );

    const activePage =
      findPageIndexAtOffset(
        pageLayout.pages,
        relTop + Math.min(container.clientHeight * 0.25, 180),
      ) + 1;

    if (isProgrammaticScroll && isProgrammaticScroll.current) return;
    setCurrentPage((prev) => (prev === activePage ? prev : activePage));
  }, [
    scrollContainerRef,
    pageLayout,
    setCurrentPage,
    isProgrammaticScroll,
    singlePageView,
    currentPage,
  ]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        computeRange();
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    computeRange();
    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [computeRange, scrollContainerRef]);

  // Recompute whenever page sizes or layouts are loaded/updated
  useEffect(() => {
    computeRange();
  }, [pdfDoc, pageLayout, computeRange]);

  // Jump to a specific page
  const scrollToPage = useCallback(
    (pageNum) => {
      const container = scrollContainerRef?.current;
      const page = pageLayout.pages[pageNum - 1];
      if (!container || !page) return;
      const target = singlePageView
        ? offsetTopRef.current
        : offsetTopRef.current + page.top;
      container.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
    },
    [scrollContainerRef, pageLayout, singlePageView],
  );

  useEffect(() => {
    // Wait for the measured layout as well; otherwise a restored page can be
    // consumed before its offset exists and the viewer falls back to page one.
    if (
      scrollTargetPage !== null &&
      pdfDoc &&
      pageLayout.pages[scrollTargetPage - 1]
    ) {
      scrollToPage(scrollTargetPage);
      onScrollTargetConsumed();
    }
  }, [
    scrollTargetPage,
    pdfDoc,
    pageLayout,
    scrollToPage,
    onScrollTargetConsumed,
  ]);

  // Text selection → annotation tip
  const handleMouseUp = useCallback(
    (e) => {
      if (noteMode) return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (!text) return;
      const range = sel.getRangeAt(0);
      const container =
        range.commonAncestorContainer?.nodeType === 1
          ? range.commonAncestorContainer
          : range.commonAncestorContainer?.parentElement;
      const pageWrapper = container?.closest("[data-pdf-page]");
      const canvas = pageWrapper?.querySelector("canvas");
      const page = pageWrapper
        ? Number(pageWrapper.dataset.pdfPage)
        : currentPage;
      const { rects, canvasOffset } = getRectsRelativeToWrap(
        range,
        pdfWrapRef.current,
        canvas,
      );
      const viewportAnchor = getViewportAnchorFromRange(range);
      setPending({
        text,
        anchorPoint: viewportAnchor,
        rects,
        canvasOffset,
        page,
      });
    },
    [noteMode, currentPage],
  );

  // Click → note pin
  const handleClick = useCallback(
    (e) => {
      if (!noteMode) return;
      if (e.target.closest("[data-notepin]")) return;
      const pageWrapper = e.target.closest("[data-pdf-page]");
      if (!pageWrapper) return;
      const canvas = pageWrapper.querySelector("canvas");
      if (!canvas) return;
      const cb = canvas.getBoundingClientRect();
      const page = Number(pageWrapper.dataset.pdfPage);
      setNotePending({
        x: e.clientX - cb.left,
        y: e.clientY - cb.top,
        anchorPoint: {
          x: e.clientX,
          y: e.clientY,
          left: e.clientX,
          bottom: e.clientY,
        },
        page,
      });
    },
    [noteMode],
  );

  const saveAnnot = ({ comment, color }) => {
    onAddAnnot({
      id: uid(),
      text: pending.text,
      comment,
      color,
      page: pending.page || currentPage,
      rects: pending.rects,
      canvasOffset: pending.canvasOffset,
      cssScale: zoom / 100,
      time: new Date().toLocaleTimeString(),
    });
    setPending(null);
    window.getSelection().removeAllRanges();
  };

  const saveNote = (text) => {
    onAddNote({
      id: uid(),
      text,
      page: notePending.page || currentPage,
      x: notePending.x,
      y: notePending.y,
      cssScale: zoom / 100,
      time: new Date().toLocaleTimeString(),
    });
    setNotePending(null);
  };

  if (loadError) {
    return (
      <ErrorBox>
        Could not load PDF.{" "}
        <a href={url} target="_blank" rel="noopener noreferrer">
          Open directly
        </a>
      </ErrorBox>
    );
  }

  return (
    <>
      <PdfWrap
        ref={pdfWrapRef}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        $noteMode={noteMode}
      >
        <PdfDocument
          file={url}
          options={documentOptions}
          onLoadSuccess={handleDocLoadSuccess}
          onLoadError={handleDocLoadError}
          loading={
            <PdfPagePlaceholder
              width={Math.round((pageSize?.width || 595) * (zoom / 100))}
              height={Math.round((pageSize?.height || 842) * (zoom / 100))}
            />
          }
        >
          {pdfDoc && pageLayout.pages.length === 0 && (
            <PdfPagePlaceholder
              width={Math.round((pageSize?.width || 595) * (zoom / 100))}
              height={Math.round((pageSize?.height || 842) * (zoom / 100))}
            />
          )}

          {pdfDoc && pageLayout.pages.length > 0 && (
            <PdfList
              style={{
                width: pageLayout.width,
                height: singlePageView
                  ? pageLayout.pages[currentPage - 1]?.height || 0
                  : pageLayout.height,
              }}
            >
              {Array.from(
                {
                  length: singlePageView
                    ? 1
                    : Math.max(0, renderRange.end - renderRange.start + 1),
                },
                (_, idx) => {
                  const pageNumber = singlePageView
                    ? currentPage
                    : renderRange.start + idx;
                  const page = pageLayout.pages[pageNumber - 1];
                  if (!page) return null;
                  return (
                    <div
                      key={`${url}-${pageNumber}`}
                      data-pdf-page={pageNumber}
                      style={{
                        position: "absolute",
                        top: singlePageView ? 0 : page.top,
                        left: (pageLayout.width - page.width) / 2,
                        width: page.width,
                        height: page.height,
                      }}
                    >
                      <VirtualPdfPage
                        pageNumber={pageNumber}
                        zoom={zoom}
                        width={page.width}
                        height={page.height}
                        annotations={annotations}
                        notes={notes}
                        onDeleteAnnot={onDeleteAnnot}
                        onDeleteNote={onDeleteNote}
                      />
                    </div>
                  );
                },
              )}
            </PdfList>
          )}
        </PdfDocument>
      </PdfWrap>

      {/* {pending && !noteMode && (
        <SelectionTip
          anchorPoint={pending.anchorPoint}
          text={pending.text}
          onSave={saveAnnot}
          onCancel={() => {
            setPending(null);
            window.getSelection().removeAllRanges();
          }}
        />
      )}
      {notePending && (
        <NoteInputTip
          anchorPoint={notePending.anchorPoint}
          page={currentPage}
          onSave={saveNote}
          onCancel={() => setNotePending(null)}
        />
      )} */}
    </>
  );
}

// ─── PdfAnnotOverlay ─────────────────────────────────────────────────────────

const PdfAnnotOverlay = React.memo(function PdfAnnotOverlay({
  ann,
  color,
  zoom,
}) {
  const factor = zoom / 100 / (ann.cssScale || 1);
  return (
    <>
      {ann.rects.map((r, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: r.left * factor,
            top: r.top * factor,
            width: r.width * factor,
            height: r.height * factor,
            background: color.alpha,
            borderBottom: `2px solid ${color.bg}`,
            pointerEvents: "none",
            mixBlendMode: "multiply",
            borderRadius: 2,
          }}
        />
      ))}
    </>
  );
});

// ─── DocxViewer ───────────────────────────────────────────────────────────────

function DocxViewer({
  url,
  annotations,
  notes,
  onAddAnnot,
  onDeleteAnnot,
  onAddNote,
  onDeleteNote,
  noteMode,
}) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const [notePending, setNotePending] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setHtml("");
    fetchDocxHtml(url)
      .then((h) => {
        setHtml(h);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [url]);

  const handleMouseUp = useCallback(() => {
    if (noteMode) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const text = sel.toString().trim();
    if (!text) return;
    const range = sel.getRangeAt(0);
    const viewportAnchor = getViewportAnchorFromRange(range);
    setPending({ text, anchorPoint: viewportAnchor });
  }, [noteMode]);

  const handleClick = useCallback(
    (e) => {
      if (!noteMode) return;
      if (e.target.closest("[data-notepin]")) return;
      const wb = wrapRef.current?.getBoundingClientRect();
      if (!wb) return;
      setNotePending({
        x: e.clientX - wb.left,
        y: e.clientY - wb.top,
        anchorPoint: {
          x: e.clientX,
          y: e.clientY,
          left: e.clientX,
          bottom: e.clientY,
        },
      });
    },
    [noteMode],
  );

  const saveAnnot = ({ comment, color }) => {
    onAddAnnot({
      id: uid(),
      text: pending.text,
      comment,
      color,
      page: null,
      rects: [],
      canvasOffset: { left: 0, top: 0 },
      cssScale: 1,
      time: new Date().toLocaleTimeString(),
    });
    setPending(null);
    window.getSelection().removeAllRanges();
  };

  const saveNote = (text) => {
    onAddNote({
      id: uid(),
      text,
      page: null,
      x: notePending.x,
      y: notePending.y,
      cssScale: 1,
      time: new Date().toLocaleTimeString(),
    });
    setNotePending(null);
  };

  const renderedHtml = useMemo(() => {
    let out = html;
    annotations.forEach((ann) => {
      const color =
        HIGHLIGHT_COLORS.find((c) => c.key === ann.color) ||
        HIGHLIGHT_COLORS[0];
      try {
        const escaped = ann.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        out = out.replace(
          new RegExp(escaped, "g"),
          `<mark data-ann-id="${ann.id}" style="background:${color.alpha};border-bottom:2px solid ${color.bg};border-radius:2px;padding:0 1px;cursor:pointer;">${ann.text}</mark>`,
        );
      } catch {}
    });
    return out;
  }, [html, annotations]);

  if (loading)
    return (
      <DocxSkeletonWrap>
        <SkeletonShimmer />
        <DocxSkeletonInner>
          {[70, 100, 95, 88, 60, 100, 96, 80, 75, 55].map((w, i) => (
            <SkeletonBar
              key={i}
              style={{ width: `${w}%`, height: 12, marginBottom: 12 }}
            />
          ))}
        </DocxSkeletonInner>
      </DocxSkeletonWrap>
    );

  if (error)
    return (
      <ErrorBox>
        Could not load DOCX: {error}
        <br />
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download
        </a>
      </ErrorBox>
    );

  const docxNotes = notes.filter((n) => n.page === null && n.x != null);

  return (
    <>
      <DocxScrollArea>
        <div style={{ position: "relative", width: "100%", maxWidth: 820 }}>
          <DocxContent
            ref={wrapRef}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
            onMouseUp={handleMouseUp}
            onClick={handleClick}
            $noteMode={noteMode}
          />
          {docxNotes.map((n) => (
            <div
              key={n.id}
              data-notepin="1"
              style={{ position: "absolute", left: n.x, top: n.y, zIndex: 50 }}
            >
              <StickyNotePin note={n} zoom={100} onDelete={onDeleteNote} />
            </div>
          ))}
        </div>
      </DocxScrollArea>

      {pending && !noteMode && (
        <SelectionTip
          anchorPoint={pending.anchorPoint}
          text={pending.text}
          onSave={saveAnnot}
          onCancel={() => {
            setPending(null);
            window.getSelection().removeAllRanges();
          }}
        />
      )}
      {notePending && (
        <NoteInputTip
          anchorPoint={notePending.anchorPoint}
          page={null}
          onSave={saveNote}
          onCancel={() => setNotePending(null)}
        />
      )}
    </>
  );
}

function ImageViewer({ url, name }) {
  return (
    <ImageViewerWrap>
      <img
        src={url}
        alt={name}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          borderRadius: 4,
          boxShadow:
            "0 2px 4px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.12), 0 20px 48px rgba(0,0,0,0.08)",
        }}
      />
    </ImageViewerWrap>
  );
}

function VideoViewer({ url, name }) {
  return (
    <MediaViewerWrap>
      <VideoCard>
        <video
          src={url}
          controls
          style={{
            width: "100%",
            maxHeight: "70vh",
            borderRadius: 8,
            outline: "none",
          }}
          controlsList="nodownload"
        />
        <MediaTitle>{name}</MediaTitle>
      </VideoCard>
    </MediaViewerWrap>
  );
}

function AudioViewer({ url, name }) {
  return (
    <MediaViewerWrap>
      <AudioCard>
        <AudioDisc>{"🎵"}</AudioDisc>
        <AudioTitle>{name}</AudioTitle>
        <audio
          src={url}
          controls
          style={{ width: "100%", maxWidth: "360px" }}
          controlsList="nodownload"
        />
      </AudioCard>
    </MediaViewerWrap>
  );
}

function TxtViewer({ url }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((t) => {
        setText(t);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [url]);

  if (loading)
    return (
      <DocxSkeletonWrap>
        <SkeletonShimmer />
      </DocxSkeletonWrap>
    );
  if (error)
    return (
      <ErrorBox>
        Could not load file: {error}
        <br />
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download
        </a>
      </ErrorBox>
    );

  return (
    <TxtContent>
      <pre>{text}</pre>
    </TxtContent>
  );
}

function OfficeViewer({ url }) {
  const viewerSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  return (
    <OfficeFrameWrap>
      <iframe
        title="office-preview"
        src={viewerSrc}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    </OfficeFrameWrap>
  );
}

// ─── RightSidePanel ───────────────────────────────────────────────────────────

const RightSidePanel = React.memo(function RightSidePanel({
  annotations,
  notes,
  onDeleteAnnot,
  onDeleteNote,
  visibility,
  onChangeVisibility,
  doc,
  numPages,
  ft,
  hideVisibility,
}) {
  const [expandedSummary, setExpandedSummary] = useState(false);
  const [expandedKeywords, setExpandedKeywords] = useState(false);
  const keywordsRef = useRef(null);
  const summaryRef = useRef(null);

  const scrollToSection = (ref) => {
    setTimeout(() => {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }, 110);
  };

  const summaryText = doc.documentSummary || "";
  const keywordsRaw = doc.keywords || [""];
  const keywordsText = Array.isArray(keywordsRaw)
    ? keywordsRaw.join(", ")
    : keywordsRaw;

  const isSummaryLong = summaryText.length > 180;
  const isKeywordsLong = keywordsText.length > 120;

  const formattedSummary = (summaryText || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  // const keywordsText = doc.keywords || "";

  return (
    <RightPanelScroll>
      <SectionBlock>
        <SectionHeader>
          <SectionTitle>Annotations</SectionTitle>
          {annotations.length > 0 && (
            <SectionBadge $color="#00818a">{annotations.length}</SectionBadge>
          )}
        </SectionHeader>
        {annotations.length === 0 ? (
          <EmptyState>
            <EmptyTitle>No annotations yet</EmptyTitle>
          </EmptyState>
        ) : (
          <CardList>
            {annotations.map((a) => {
              const c =
                HIGHLIGHT_COLORS.find((x) => x.key === a.color) ||
                HIGHLIGHT_COLORS[0];
              return (
                <AnnotCard key={a.id}>
                  <AnnotColorStrip style={{ background: c.bg }} />
                  <AnnotCardBody>
                    <AnnotCardTop>
                      <AnnotSnippet>
                        "{a.text.slice(0, 60)}
                        {a.text.length > 60 ? "…" : ""}"
                      </AnnotSnippet>
                      <DeleteBtn
                        onClick={() => onDeleteAnnot(a.id)}
                        title="Delete"
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </DeleteBtn>
                    </AnnotCardTop>
                    {a.comment && <AnnotComment>{a.comment}</AnnotComment>}
                    <AnnotFooter>
                      <FooterChip>
                        {a.page ? `Pg ${a.page}` : "DOCX"}
                      </FooterChip>
                      <FooterChip $color={c.bg}>{c.label}</FooterChip>
                      {a.time && <FooterTime>{a.time}</FooterTime>}
                    </AnnotFooter>
                  </AnnotCardBody>
                </AnnotCard>
              );
            })}
          </CardList>
        )}
      </SectionBlock>

      <SectionDivider />

      <SectionBlock>
        <SectionHeader>
          <SectionTitle>Notes</SectionTitle>
          {notes.length > 0 && (
            <SectionBadge $color="#B45309">{notes.length}</SectionBadge>
          )}
        </SectionHeader>
        {notes.length === 0 ? (
          <EmptyState>
            <EmptyTitle>No notes yet</EmptyTitle>
          </EmptyState>
        ) : (
          <CardList>
            {notes.map((n) => (
              <NoteCard key={n.id}>
                <NoteCardTop>
                  <NoteCardMeta>
                    <FooterChip $yellow>
                      {n.page ? `Pg ${n.page}` : "Document"}
                    </FooterChip>
                    {n.time && <FooterTime>{n.time}</FooterTime>}
                  </NoteCardMeta>
                  <DeleteBtn onClick={() => onDeleteNote(n.id)} title="Delete">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </DeleteBtn>
                </NoteCardTop>
                <NoteCardText>{n.text}</NoteCardText>
              </NoteCard>
            ))}
          </CardList>
        )}
      </SectionBlock>

      <SectionDivider />

      <SectionBlock>
        <SectionHeader>
          <SectionTitle>Properties</SectionTitle>
        </SectionHeader>
        <InfoList>
          <InfoItem>
            <InfoKey>Name</InfoKey>
            <InfoVal title={doc.name}>
              {doc.name.length > 32 ? doc.name.slice(0, 32) + "…" : doc.name}
            </InfoVal>
          </InfoItem>
          {getExhibitId(doc) && (
            <InfoItem>
              <InfoKey>Exhibit ID</InfoKey>
              <InfoVal>{getExhibitId(doc)}</InfoVal>
            </InfoItem>
          )}
          {Array.isArray(keywordsRaw) ? (
            <InfoItem ref={keywordsRef}>
              <InfoKey>Keywords</InfoKey>
              <div>
                {keywordsRaw.map((kw, idx) => (
                  <TruncatedText key={idx} $expanded={expandedKeywords}>
                    {isKeywordsLong && !expandedKeywords
                      ? kw.slice(0, 120) + "…"
                      : kw}
                  </TruncatedText>
                ))}
                {isKeywordsLong && (
                  <ExpandLink
                    onClick={() => {
                      setExpandedKeywords((p) => {
                        const n = !p;
                        if (!p) scrollToSection(keywordsRef);
                        return n;
                      });
                    }}
                  >
                    {expandedKeywords ? "Show less" : "Show all"}
                  </ExpandLink>
                )}
              </div>
            </InfoItem>
          ) : (
            <InfoItem ref={keywordsRef}>
              <InfoKey>Keywords</InfoKey>
              <div>
                <TruncatedText $expanded={expandedKeywords}>
                  {isKeywordsLong && !expandedKeywords
                    ? keywordsText.slice(0, 120) + "…"
                    : keywordsText}
                </TruncatedText>
                {isKeywordsLong && (
                  <ExpandLink
                    onClick={() => {
                      setExpandedKeywords((p) => {
                        const n = !p;
                        if (!p) scrollToSection(keywordsRef);
                        return n;
                      });
                    }}
                  >
                    {expandedKeywords ? "Show less" : "Show all"}
                  </ExpandLink>
                )}
              </div>
            </InfoItem>
          )}
          {summaryText && (
            <InfoItem ref={summaryRef}>
              <InfoKey>Summary</InfoKey>
              <div>
                <TruncatedText $expanded={expandedSummary}>
                  {isSummaryLong && !expandedSummary
                    ? formattedSummary.slice(0, 180) + "..."
                    : formattedSummary}
                </TruncatedText>
                {isSummaryLong && (
                  <ExpandLink
                    onClick={() => {
                      setExpandedSummary((p) => {
                        const n = !p;
                        if (!p) scrollToSection(summaryRef);
                        return n;
                      });
                    }}
                  >
                    {expandedSummary ? "Show less" : "Read full summary"}
                  </ExpandLink>
                )}
              </div>
            </InfoItem>
          )}
          <InfoItem>
            <InfoKey>Format</InfoKey>
            <InfoVal>
              <FormatPill $ft={ft}>{ft.toUpperCase()}</FormatPill>
            </InfoVal>
          </InfoItem>
          {numPages && (
            <InfoItem>
              <InfoKey>Pages</InfoKey>
              <InfoVal>{numPages}</InfoVal>
            </InfoItem>
          )}
          <InfoItem>
            <InfoKey>Annotations</InfoKey>
            <InfoVal>
              <StatBadge $color="#00818a">{annotations.length}</StatBadge>
            </InfoVal>
          </InfoItem>
          <InfoItem>
            <InfoKey>Notes</InfoKey>
            <InfoVal>
              <StatBadge $color="#B45309">{notes.length}</StatBadge>
            </InfoVal>
          </InfoItem>
        </InfoList>
      </SectionBlock>

      <SectionDivider />
    </RightPanelScroll>
  );
});

// ─── DownloadMenu ─────────────────────────────────────────────────────────────

function DownloadMenu({ open, doc, annotations, notes, onClose, isPdf }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  if (!open) return null;
  const dlOriginal = () => {
    const a = document.createElement("a");
    a.href = doc.s3url;
    a.download = doc.name;
    a.target = "_blank";
    a.click();
    onClose();
  };
  const dlAnnotatedPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadPdfWithAnnotations(doc.s3url, annotations, notes, doc.name);
    } catch (e) {
      alert("Could not generate annotated PDF: " + e.message);
    }
    setPdfLoading(false);
    onClose();
  };
  return (
    <DlDropdown>
      <DlItem onClick={dlOriginal}>
        <div>
          <DlItemTitle>Original document</DlItemTitle>
          <DlItemSub>Download unmodified file</DlItemSub>
        </div>
      </DlItem>
      {isPdf && (
        <DlItem onClick={dlAnnotatedPdf} disabled={pdfLoading}>
          <div>
            <DlItemTitle>
              {pdfLoading ? "Generating…" : "PDF with highlights & notes"}
            </DlItemTitle>
            <DlItemSub>Bakes all annotations into the PDF</DlItemSub>
          </div>
        </DlItem>
      )}
    </DlDropdown>
  );
}

const getOwnerBadgeStyle = (ownerType) => {
  const type = String(ownerType || "Neutral").toLowerCase();
  if (type.includes("claimant")) return { bg: "#e6fffa", color: "#0d9488" }; // teal
  if (type.includes("respondent")) return { bg: "#eff6ff", color: "#2563eb" }; // blue
  if (type.includes("manager") || type.includes("casemanager"))
    return { bg: "#faf5ff", color: "#7c3aed" }; // purple
  return { bg: "#f1f5f9", color: "#475569" }; // neutral
};

const getOwnerRoleLabel = (ownerType) => {
  const type = String(ownerType || "").toLowerCase();
  if (type.includes("claimant")) return "Claimant";
  if (type.includes("respondent")) return "Respondent";
  if (type.includes("manager") || type.includes("casemanager"))
    return "Case Manager";
  if (type.includes("mediator")) return "Mediator";
  if (type.includes("arbitrator")) return "Arbitrator";
  return ownerType || "Neutral";
};

const getVisibilityBadgeStyle = (type) => {
  const t = String(type || "me").toLowerCase();
  if (t === "me") return { label: "Only Me", bg: "#fef2f2", color: "#dc2626" }; // red
  if (t === "strictmode")
    return { label: "Strict Mode", bg: "#fff7ed", color: "#ea580c" }; // orange
  if (t === "partyname")
    return { label: "Visible to Parties", bg: "#ecfdf5", color: "#059669" }; // green
  if (t === "all")
    return { label: "Visible to All", bg: "#f0fdf4", color: "#16a34a" }; // light green
  return { label: type || "Only Me", bg: "#f1f5f9", color: "#475569" };
};

const parsePageCount = (pages) => {
  if (pages == null) return 0;
  const str = String(pages).trim();
  if (!str) return 0;
  return str.split(",").reduce((total, part) => {
    const segment = part.trim();
    if (!segment) return total;
    const rangeMatch = segment.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
    if (rangeMatch) {
      const start = Number(rangeMatch[1]);
      const end = Number(rangeMatch[2]);
      if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start)
        return total + (end - start + 1);
    }
    const singleVal = Number(segment);
    if (!Number.isNaN(singleVal)) return total + 1;
    return total;
  }, 0);
};

// ─── Main DocumentViewer ──────────────────────────────────────────────────────

export default function DocumentViewer({
  doc,
  onBack,
  openTabs,
  onTabClick,
  onTabClose,
  id,
  setTrigger,
  fetchAllBundles,
  activeSection,
  trialBundles = [],
  onCompareToggle,
  onPrevDoc,
  onNextDoc,
  hasPrevDoc = false,
  hasNextDoc = false,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [zoom, setZoom] = useState(90);
  const [rightOpen, setRightOpen] = useState(false);
  const [annotations, setAnnotations] = useState(() => loadAnnotations(doc.id));
  const [notes, setNotes] = useState(() => loadNotes(doc.id));
  const [noteMode, setNoteMode] = useState(false);
  const [scrollTargetPage, setScrollTargetPage] = useState(null);
  const [singlePageView, setSinglePageView] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { activeDoc } = useLoader();
  const isProgrammaticScrollRef = useRef(false);
  const suppressAutoFitRef = useRef(false);

  // Page dimensions for dynamic zoom fitting
  const [pageSize, setPageSize] = useState({ width: 595, height: 842 });
  const [comparePageSize, setComparePageSize] = useState({
    width: 595,
    height: 842,
  });

  useEffect(() => {
    setPageInputValue(currentPage);
  }, [currentPage]);

  // Comparison mode states
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareDoc, setCompareDoc] = useState(null);
  const [activeCompareSide, setActiveCompareSide] = useState("original");
  const [compareAnnotations, setCompareAnnotations] = useState([]);
  const [compareNotes, setCompareNotes] = useState([]);

  useEffect(() => {
    if (compareOpen && compareDoc) {
      setActiveCompareSide("original");
    }
  }, [compareOpen, compareDoc, doc]);

  useEffect(() => {
    if (compareDoc?.id) {
      setCompareAnnotations(loadAnnotations(compareDoc.id));
      setCompareNotes(loadNotes(compareDoc.id));
    } else {
      setCompareAnnotations([]);
      setCompareNotes([]);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id]);

  useEffect(() => {
    if (compareDoc?.id) {
      saveAnnotations(compareDoc.id, compareAnnotations);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareAnnotations]);

  useEffect(() => {
    if (compareDoc?.id) {
      saveNotes(compareDoc.id, compareNotes);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareNotes]);

  useEffect(() => {
    if (!doc?.s3url) return;
    let link;
    try {
      const origin = new URL(doc.s3url).origin;
      link = document.createElement("link");
      link.rel = "preconnect";
      link.href = origin;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    } catch {}
    return () => {
      if (link) document.head.removeChild(link);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.s3url]);

  const [coreCompareDocs, setCoreCompareDocs] = useState([]);
  const [loadingCompareDocs, setLoadingCompareDocs] = useState(false);
  const [comparePage, setComparePage] = useState(1);
  const [compareNumPages, setCompareNumPages] = useState(null);
  const [compareZoom, setCompareZoom] = useState(90);
  const [compareScrollTargetPage, setCompareScrollTargetPage] = useState(null);
  const [compareSinglePageView, setCompareSinglePageView] = useState(false);
  const [comparePageInputValue, setComparePageInputValue] = useState(1);
  useEffect(() => {
    setComparePageInputValue(comparePage);
  }, [comparePage]);
  const [showSelectCompareModal, setShowSelectCompareModal] = useState(false);
  const [compareSearchQuery, setCompareSearchQuery] = useState("");
  const [wasRightPanelOpenBeforeCompare, setWasRightPanelOpenBeforeCompare] =
    useState(false);
  const compareCanvasRef = useRef(null);
  const isCompareProgrammaticScrollRef = useRef(false);

  const dlRef = useRef(null);
  const zoomRef = useRef(null);
  const appRef = useRef(null);
  const canvasRef = useRef(null); // scroll container
  const zoomAnchorRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  const ft = detectFileType(doc.s3url);
  const isPdf = ft === "pdf";
  const isDocx = ft === "docx"; // only true .docx goes through mammoth now
  const isLegacyDoc = ft === "doc"; // old binary .doc → Office Online viewer instead
  const isImage = ft === "image";
  const isTxt = ft === "txt";
  const isOffice = ft === "office";
  const isVideo = ft === "video";
  const isAudio = ft === "audio";
  const documentPagePositionKey = getDocumentPagePositionKey(doc);

  // Save the current reading position before this document is replaced (or the
  // viewer unmounts). Effect cleanup uses the values from the correct document.
  useEffect(() => {
    if (!isPdf || !documentPagePositionKey) return undefined;
    return () => {
      documentPagePositions.set(documentPagePositionKey, {
        page: currentPage,
        zoom,
        singlePageView,
      });
    };
  }, [currentPage, documentPagePositionKey, isPdf, singlePageView, zoom]);

  // Fetch all Core Bundle PDFs for comparison
  const fetchAllCoreDocs = useCallback(async () => {
    if (!id) return;
    setLoadingCompareDocs(true);
    try {
      const res = await CaseService.getcaseDocuments(
        id,
        "?page=1&perPage=1000",
      );
      if (res?.data && Array.isArray(res.data)) {
        const pdfDocs = res.data
          .map((d) => {
            const rawPages =
              d.pageCount ||
              d.pages ||
              d.totalPages ||
              d.page_count ||
              d.totalPageCount;
            const tabNum = d.tabNum ?? d.tab_num ?? d.tabNumber ?? null;
            const rawTabRef =
              d.tabRef ||
              d.tab_ref ||
              d.tabRefNumber ||
              d.refNumber ||
              d.ref ||
              d.reference ||
              d.display_tab ||
              d.displayTab ||
              "";
            const tabRef = rawTabRef;
            return {
              id: d.id,
              name: d.name,
              s3url: d.url?.url || d.fileUrl || d.url,
              source: d.ownerType || "Neutral",
              ownerName:
                d.owner?.name || d.uploadedBy?.name || d.ownerType || "Neutral",
              ownerType: d.ownerType,
              visibilityType: d.visibilityType || "all",
              pageCount: rawPages ? Number(rawPages) : null,
              tabNum,
              bundleCode: "",
              tabRef,
            };
          })
          .filter((d) => d.s3url && detectFileType(d.s3url) === "pdf");
        setCoreCompareDocs(pdfDocs);

        // Asynchronously probe page counts for PDFs that don't have it
        const needsProbe = pdfDocs.filter((d) => d.pageCount === null);
        if (needsProbe.length > 0) {
          const CONCURRENCY = 2;
          const queue = [...needsProbe];
          const runProbing = async () => {
            while (queue.length > 0) {
              const batch = queue.splice(0, CONCURRENCY);
              const results = await Promise.all(
                batch.map(async (d) => {
                  try {
                    const pdf = await PDFJS.getDocument({
                      url: d.s3url,
                      disableAutoFetch: false,
                      disableRange: true,
                    }).promise;
                    return { id: d.id, pageCount: pdf.numPages };
                  } catch {
                    return { id: d.id, pageCount: null };
                  }
                }),
              );
              setCoreCompareDocs((prev) =>
                prev.map((doc) => {
                  const match = results.find((r) => r.id === doc.id);
                  return match && match.pageCount !== null
                    ? { ...doc, pageCount: match.pageCount }
                    : doc;
                }),
              );
            }
          };
          runProbing();
        }
      }
    } catch (err) {
      enqueueSnackbar("Failed to fetch documents for comparison", {
        variant: "error",
      });
    } finally {
      setLoadingCompareDocs(false);
    }
  }, [id, enqueueSnackbar]);

  // Extract Trial Bundle PDFs
  const trialPdfDocs = useMemo(() => {
    const list = [];
    (trialBundles || []).forEach((bundle) => {
      const bCode = bundle.bundleCode || bundle.code || bundle.id || "";
      (bundle.bundles || []).forEach((sf) => {
        (sf.assignments || []).forEach((a) => {
          const s3url = a.bundleDocumentUrl || a.fileUrl || a.url;
          if (s3url && detectFileType(s3url) === "pdf") {
            const tabNum = a.tabNum ?? a.tab_num ?? a.tabNumber ?? "";
            const rawTabRef =
              a.tabRef ||
              a.tab_ref ||
              a.tabRefNumber ||
              a.refNumber ||
              a.ref ||
              a.reference ||
              a.display_tab ||
              a.displayTab ||
              a.tab_code ||
              a.tabCode ||
              "";
            const tabRef =
              rawTabRef ||
              (bCode && tabNum !== "" ? `${bCode}/${tabNum}` : "") ||
              (sf.code &&
              sf.code !== "__main__" &&
              sf.code !== "__flat__" &&
              tabNum !== ""
                ? `${sf.code}/${tabNum}`
                : "") ||
              (tabNum !== "" ? `${tabNum}` : "");
            list.push({
              id: `trial-${bundle.id}-${a.id || a.sourceDocId || Math.random()}-${a.tabNum}`,
              name: a.userLabel || a.name || `Tab ${a.tabNum}`,
              s3url,
              bundleName: bundle.name,
              bundleCode: bCode,
              folderName: sf.name || sf.code,
              tabNum: a.tabNum,
              tabRef: tabRef,
              visibilityType:
                a.visibilityType ||
                sf.visibilityType ||
                bundle.visibilityType ||
                "me",
              pageCount: parsePageCount(a.pages),
            });
          }
        });
      });
    });
    return list;
  }, [trialBundles]);

  // Filter comparison documents based on search query
  const filteredCoreDocs = useMemo(() => {
    const query = compareSearchQuery.trim().toLowerCase();
    if (!query) return coreCompareDocs;
    return coreCompareDocs.filter((d) => {
      const docName = (d.name || "").toLowerCase();
      const tabRef = (d.tabRef || "").toLowerCase();

      return docName.includes(query) || (tabRef && tabRef.includes(query));
    });
  }, [coreCompareDocs, compareSearchQuery]);

  const filteredTrialDocs = useMemo(() => {
    const query = compareSearchQuery.trim().toLowerCase();
    if (!query) return trialPdfDocs;
    return trialPdfDocs.filter((d) => {
      const docName = (d.name || "").toLowerCase();
      const bundleName = (d.bundleName || "").toLowerCase();
      const folderName = (d.folderName || "").toLowerCase();
      const tabRef = (d.tabRef || "").toLowerCase();
      const tabNumStr = d.tabNum != null ? String(d.tabNum).toLowerCase() : "";
      const bundleCode = (d.bundleCode || "").toLowerCase();
      const fullRef =
        bundleCode && tabNumStr ? `${bundleCode}/${tabNumStr}` : "";

      return (
        docName.includes(query) ||
        bundleName.includes(query) ||
        folderName.includes(query) ||
        tabRef.includes(query) ||
        (tabNumStr && tabNumStr.includes(query)) ||
        (fullRef && fullRef.includes(query)) ||
        (bundleCode && bundleCode.includes(query))
      );
    });
  }, [trialPdfDocs, compareSearchQuery]);

  // Reset on doc change
  useEffect(() => {
    const savedPosition = documentPagePositions.get(
      getDocumentPagePositionKey(doc),
    );
    const savedPage = savedPosition?.page || 1;

    setCurrentPage(savedPage);
    setPageInputValue(savedPage);
    setNumPages(null);
    setZoom(savedPosition?.zoom || 90);
    setNoteMode(false);
    setScrollTargetPage(savedPosition ? savedPage : null);
    setSinglePageView(savedPosition?.singlePageView || false);
    setAnnotations(loadAnnotations(doc.id));
    setNotes(loadNotes(doc.id));
    setCompareDoc(null);
    setCompareOpen(false);
    setCompareSearchQuery("");
    setCompareSinglePageView(false);
    setCompareAnnotations([]);
    setCompareNotes([]);
    setPageSize({ width: 595, height: 842 });
    setComparePageSize({ width: 595, height: 842 });
    setComparePageInputValue(1); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id, doc.s3url]);

  const fitMainPage = useCallback(() => {
    const container = canvasRef.current;
    if (!container || !pageSize) return;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth > 0 && containerHeight > 0) {
      const style = window.getComputedStyle(container);
      const padTop = parseFloat(style.paddingTop) || 0;
      const padBottom = parseFloat(style.paddingBottom) || 0;
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      const availableWidth = containerWidth - padLeft - padRight;
      const availableHeight = containerHeight - padTop - padBottom;
      const scaleX = availableWidth / pageSize.width;
      const scaleY = availableHeight / pageSize.height;
      const fitZoom = Math.min(scaleX, scaleY) * 100;
      setZoom(Math.min(220, Math.max(30, Math.round(fitZoom))));
    }
  }, [pageSize]);

  const fitComparePage = useCallback(() => {
    const container = compareCanvasRef.current;
    if (!container || !comparePageSize) return;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth > 0 && containerHeight > 0) {
      const style = window.getComputedStyle(container);
      const padTop = parseFloat(style.paddingTop) || 0;
      const padBottom = parseFloat(style.paddingBottom) || 0;
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      const availableWidth = containerWidth - padLeft - padRight;
      const availableHeight = containerHeight - padTop - padBottom;
      const scaleX = availableWidth / comparePageSize.width;
      const scaleY = availableHeight / comparePageSize.height;
      const fitZoom = Math.min(scaleX, scaleY) * 100;
      setCompareZoom(Math.min(220, Math.max(30, Math.round(fitZoom))));
    }
  }, [comparePageSize]);

  const handleFitWidth = () => {
    const container = canvasRef.current;
    if (!container || !pageSize) return;
    const containerWidth = container.clientWidth;
    if (containerWidth > 0) {
      const padding = 72;
      const fitZoom = ((containerWidth - padding) / pageSize.width) * 100;
      setZoom(Math.min(220, Math.max(30, Math.round(fitZoom))));
    }
  };

  const handleFitPage = () => {
    fitMainPage();
    if (compareOpen) {
      fitComparePage();
    }
  };

  useEffect(() => {
    saveAnnotations(doc.id, annotations);
  }, [doc.id, annotations]);
  useEffect(() => {
    saveNotes(doc.id, notes);
  }, [doc.id, notes]);

  useEffect(() => {
    if (compareDoc?.id) saveAnnotations(compareDoc.id, compareAnnotations); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareAnnotations]);
  useEffect(() => {
    if (compareDoc?.id) saveNotes(compareDoc.id, compareNotes); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareDoc?.id, compareNotes]);

  // Automatically fit pages whenever the scroll containers change dimensions
  useEffect(() => {
    const mainEl = canvasRef.current;
    const compareEl = compareCanvasRef.current;
    let rafId = null;
    let timeoutId = null;

    const observer = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      // debounce 120ms so continuous panel-resize/drag doesn't spam fitPage
      timeoutId = setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          if (suppressAutoFitRef.current) return;
          if (mainEl) fitMainPage();
          if (compareEl) fitComparePage();
        });
      }, 120);
    });

    if (mainEl) observer.observe(mainEl);
    if (compareEl) observer.observe(compareEl);

    fitMainPage();
    fitComparePage();

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fitMainPage, fitComparePage, compareOpen, isFullscreen]);

  // Automatically close side panel when compare view is opened, and reopen it when closed if it was open
  useEffect(() => {
    if (compareOpen) {
      if (rightOpen) {
        setWasRightPanelOpenBeforeCompare(true);
        setRightOpen(false);
      } else {
        setWasRightPanelOpenBeforeCompare(false);
      }
    } else {
      if (wasRightPanelOpenBeforeCompare) {
        setRightOpen(true);
        setWasRightPanelOpenBeforeCompare(false);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareOpen]);

  // Notify parent component (CoreBundle) when compare view is toggled
  useEffect(() => {
    if (typeof onCompareToggle === "function") {
      onCompareToggle(compareOpen);
    }
    return () => {
      if (typeof onCompareToggle === "function") {
        onCompareToggle(false);
      }
    };
  }, [compareOpen, onCompareToggle]);

  // Close dropdowns on outside click
  useEffect(() => {
    const h = (e) => {
      if (dlRef.current && !dlRef.current.contains(e.target)) setDlOpen(false);
      if (zoomRef.current && !zoomRef.current.contains(e.target))
        setZoomMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Fullscreen & escape key
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (noteMode) {
          setNoteModeState(false);
          return;
        }
        if (document.fullscreenElement)
          document.exitFullscreen().catch(() => {});
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("keydown", onKeyDown);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteMode]);

  useEffect(() => {
    if (isFullscreen) {
      setZoom(100);
    } else {
      setZoom(90);
    }
  }, [isFullscreen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && activeDoc) onBack();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDoc]);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await appRef.current?.requestFullscreen();
      } catch {}
    } else {
      try {
        await document.exitFullscreen();
      } catch {}
    }
  };

  const setNoteModeState = useCallback((value) => {
    suppressAutoFitRef.current = true;
    setNoteMode((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      return next;
    });
    window.requestAnimationFrame(() => {
      suppressAutoFitRef.current = false;
    });
  }, []);

  // Annotation / note CRUD
  const addAnnot = useCallback((a) => setAnnotations((p) => [a, ...p]), []);
  const deleteAnnot = useCallback(
    (id) => setAnnotations((p) => p.filter((x) => x.id !== id)),
    [],
  );
  const addNote = useCallback(
    (n) => {
      setNotes((p) => [n, ...p]);
      setNoteModeState(false);
    },
    [setNoteModeState],
  );
  const deleteNote = useCallback(
    (id) => setNotes((p) => p.filter((x) => x.id !== id)),
    [],
  );

  const addCompareAnnot = useCallback(
    (a) => setCompareAnnotations((p) => [a, ...p]),
    [],
  );
  const deleteCompareAnnot = useCallback(
    (id) => setCompareAnnotations((p) => p.filter((x) => x.id !== id)),
    [],
  );
  const addCompareNote = useCallback(
    (n) => {
      setCompareNotes((p) => [n, ...p]);
      setNoteModeState(false);
    },
    [setNoteModeState],
  );
  const deleteCompareNote = useCallback(
    (id) => setCompareNotes((p) => p.filter((x) => x.id !== id)),
    [],
  );

  const isCompareActive =
    compareOpen && compareDoc && activeCompareSide === "compare";
  const activeDocObj = isCompareActive ? compareDoc : doc;
  const activeAnnotations = isCompareActive ? compareAnnotations : annotations;
  const activeNotes = isCompareActive ? compareNotes : notes;
  const activeDeleteAnnot = isCompareActive ? deleteCompareAnnot : deleteAnnot;
  const activeDeleteNote = isCompareActive ? deleteCompareNote : deleteNote;
  const activeNumPages = isCompareActive ? compareNumPages : numPages;
  const activeFt = detectFileType(activeDocObj?.s3url);

  // Zoom with viewport anchor
  const rememberViewportCenter = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    zoomAnchorRef.current = {
      x:
        el.scrollWidth > 0
          ? (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth
          : 0.5,
      y:
        el.scrollHeight > 0
          ? (el.scrollTop + el.clientHeight / 2) / el.scrollHeight
          : 0.5,
    };
  }, []);

  useLayoutEffect(() => {
    const el = canvasRef.current;
    const anchor = zoomAnchorRef.current;
    if (!el || !anchor) return;
    el.scrollTo({
      left: anchor.x * el.scrollWidth - el.clientWidth / 2,
      top: anchor.y * el.scrollHeight - el.clientHeight / 2,
      behavior: "auto",
    });
  }, [zoom]);

  const animateZoom = (target) => {
    rememberViewportCenter();
    setZoom(target);
  };

  const handleZoomIn = () =>
    animateZoom(Math.min(220, Math.round(zoom / 10) * 10 + 10));
  const handleZoomOut = () =>
    animateZoom(Math.max(30, Math.round(zoom / 10) * 10 - 10));
  const handleSetZoom = (v) => animateZoom(v);

  // Page navigation — clamps value and signals PdfViewer to scroll
  const goToPage = useCallback(
    (page) => {
      if (!isPdf) return;
      const maxPage = numPages || 1;
      const nextPage = Math.min(maxPage, Math.max(1, Number(page) || 1));

      isProgrammaticScrollRef.current = true;
      setCurrentPage(nextPage);
      setPageInputValue(nextPage);
      setSinglePageView(true);
      setScrollTargetPage(nextPage);

      // Reset programmatic scroll flag after scroll animation completes
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 800);
    },
    [isPdf, numPages],
  );

  const handlePageInputChange = useCallback((event) => {
    // A text field avoids native number-input quirks where multi-digit page
    // numbers can lose their selection while the value is being edited.
    setPageInputValue(event.target.value.replace(/\D/g, ""));
  }, []);

  const commitPageInput = useCallback(() => {
    const page = Number(pageInputValue);
    if (page >= 1 && page <= (numPages || 1)) {
      goToPage(page);
    } else {
      setPageInputValue(currentPage);
    }
  }, [currentPage, goToPage, numPages, pageInputValue]);

  const togglePageView = useCallback(() => {
    // Keep the page the user is reading in view while changing layouts.
    setSinglePageView((isSinglePage) => !isSinglePage);
    setScrollTargetPage(currentPage);
  }, [currentPage]);

  const toggleComparePageView = useCallback(() => {
    setCompareSinglePageView((v) => !v);
    setCompareScrollTargetPage(comparePage);
  }, [comparePage]);

  // Keep split-pane page navigation identical to the original viewer: entering
  // a page number (or using arrows) focuses that page, while the view-mode
  // button lets the user return to continuous scrolling.
  const goToComparePage = useCallback(
    (page) => {
      const maxPage = compareNumPages || 1;
      const nextPage = Math.min(maxPage, Math.max(1, Number(page) || 1));

      isCompareProgrammaticScrollRef.current = true;
      setComparePage(nextPage);
      setComparePageInputValue(nextPage);
      setCompareSinglePageView(true);
      setCompareScrollTargetPage(nextPage);

      setTimeout(() => {
        isCompareProgrammaticScrollRef.current = false;
      }, 800);
    },
    [compareNumPages],
  );

  const handleComparePageInputChange = useCallback((event) => {
    setComparePageInputValue(event.target.value.replace(/\D/g, ""));
  }, []);

  const commitComparePageInput = useCallback(() => {
    const page = Number(comparePageInputValue);
    if (page >= 1 && page <= (compareNumPages || 1)) {
      goToComparePage(page);
    } else {
      setComparePageInputValue(comparePage);
    }
  }, [compareNumPages, comparePage, comparePageInputValue, goToComparePage]);

  const getTabKey = (tab) =>
    [
      tab?.id,
      tab?.sourceDocId,
      tab?.bundlecode,
      tab?.bundleId,
      tab?.s3url,
      tab?.name,
    ]
      .filter((value) => value !== undefined && value !== null && value !== "")
      .join("::");

  const changeVisibility = async (value) => {
    try {
      setLoader({ state: true, message: "Updating visibility…" });
      let res;
      if (doc?.bundle) {
        res = await BundleService.update_bundle(doc.id, {
          visibilityType: value,
          sourceDocId: doc.sourceDocId,
          prefix: doc.bundlecode,
        });
        if (typeof fetchAllBundles === "function") fetchAllBundles();
      } else {
        res = await CaseService.changeDocType(
          { visibilityType: value },
          id,
          doc.id,
        );
        if (typeof setTrigger === "function") setTrigger((t) => !t);
      }
      if (res) enqueueSnackbar("Visibility updated", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <AppWrapper ref={appRef} $fullscreen={isFullscreen}>
      {/* ── Tab bar ── */}
      <TabBar>
        <BackButton onClick={onBack}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </BackButton>
        <TabDivider />
        <TabScroll>
          {(() => {
            const isCompareMode = compareOpen && compareDoc;
            const isCompareDocInOpenTabs =
              isCompareMode &&
              openTabs.some((t) => getTabKey(t) === getTabKey(compareDoc));

            const renderCompareTab = (cDoc) => (
              <DocTab
                key={`compare-${getTabKey(cDoc)}`}
                $active={isCompareMode && activeCompareSide === "compare"}
                onClick={() => {
                  if (isCompareMode) {
                    setActiveCompareSide("compare");
                  }
                }}
              >
                <TabFilePill $ft={detectFileType(cDoc.s3url)}>
                  {detectFileType(cDoc.s3url) === "pdf" ? "PDF" : "DOC"}
                </TabFilePill>
                <TabName>
                  {cDoc.name.length > 24
                    ? cDoc.name.slice(0, 24) + "…"
                    : cDoc.name}
                </TabName>
                <TabCloseBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    setCompareDoc(null);
                    setCompareOpen(false);
                  }}
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </TabCloseBtn>
              </DocTab>
            );

            if (!openTabs || openTabs.length === 0) {
              return isCompareMode ? renderCompareTab(compareDoc) : null;
            }

            return openTabs.map((t) => {
              const isMainDoc = getTabKey(t) === getTabKey(doc);
              const isCompDoc =
                isCompareMode && getTabKey(t) === getTabKey(compareDoc);

              if (isCompDoc && !isMainDoc) {
                return renderCompareTab(t);
              }

              const isActive = isCompareMode
                ? isMainDoc && activeCompareSide === "original"
                : isMainDoc;

              return (
                <React.Fragment key={`tab-group-${getTabKey(t)}`}>
                  <DocTab
                    key={getTabKey(t)}
                    $active={isActive}
                    onClick={() => {
                      if (isCompareMode && isMainDoc) {
                        setActiveCompareSide("original");
                      } else {
                        onTabClick(t);
                      }
                    }}
                  >
                    <TabFilePill $ft={detectFileType(t.s3url)}>
                      {detectFileType(t.s3url) === "pdf" ? "PDF" : "DOC"}
                    </TabFilePill>
                    <TabName>
                      {t.name.length > 24 ? t.name.slice(0, 24) + "…" : t.name}
                    </TabName>
                    <TabCloseBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        onTabClose(t);
                      }}
                    >
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </TabCloseBtn>
                  </DocTab>
                  {isMainDoc &&
                    isCompareMode &&
                    !isCompareDocInOpenTabs &&
                    renderCompareTab(compareDoc)}
                </React.Fragment>
              );
            });
          })()}
        </TabScroll>
      </TabBar>

      {/* ── Body ── */}
      <ViewerBody>
        <MainArea>
          {/* ── Toolbar ── */}
          <Toolbar>
            <ToolLeft>
              {isPdf && !(compareOpen && compareDoc) && (
                <>
                  <PageNav>
                    <PageBtn
                      onClick={onPrevDoc}
                      disabled={!hasPrevDoc}
                      title="Previous document"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="11 18 5 12 11 6" />
                        <polyline points="18 18 12 12 18 6" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      onClick={() => goToPage(1)}
                      disabled={currentPage <= 1}
                      title="First page"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <line x1="6" y1="6" x2="6" y2="18" />
                        <polyline points="17 18 11 12 17 6" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      title="Previous page"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </PageBtn>
                    <PageInput
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={pageInputValue}
                      onChange={handlePageInputChange}
                      onFocus={(e) => e.currentTarget.select()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          commitPageInput();
                          const target = e.currentTarget;
                          setTimeout(() => {
                            if (target) {
                              target.focus();
                              target.select();
                            }
                          }, 0);
                        }
                      }}
                      onBlur={commitPageInput}
                    />
                    {numPages && <PageTotal>/ {numPages}</PageTotal>}
                    <PageBtn
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={!numPages || currentPage >= numPages}
                      title="Next page"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      onClick={() => numPages && goToPage(numPages)}
                      disabled={!numPages || currentPage >= numPages}
                      title="Last page"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="7 18 13 12 7 6" />
                        <line x1="18" y1="6" x2="18" y2="18" />
                      </svg>
                    </PageBtn>
                    <PageBtn
                      onClick={onNextDoc}
                      disabled={!hasNextDoc}
                      title="Next document"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="6 18 12 12 6 6" />
                        <polyline points="13 18 19 12 13 6" />
                      </svg>
                    </PageBtn>
                  </PageNav>
                  <ViewModeBtn
                    onClick={togglePageView}
                    title={
                      singlePageView
                        ? "Show all pages in a continuous scroll"
                        : "Focus on the current page"
                    }
                  >
                    {singlePageView ? "Scroll View" : "Page View"}
                  </ViewModeBtn>
                  <ToolDivider />
                </>
              )}

              {!(compareOpen && compareDoc) && (
                <ZoomGroup>
                  <ZoomBtn
                    onClick={handleZoomOut}
                    disabled={zoom <= 30}
                    title="Zoom out"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </ZoomBtn>
                  <ZoomWrap ref={zoomRef}>
                    <ZoomLabel onClick={() => setZoomMenuOpen((o) => !o)}>
                      {Math.round(zoom)}%
                    </ZoomLabel>
                    {zoomMenuOpen && (
                      <ZoomDropdown>
                        <ZoomItem
                          onClick={() => {
                            handleFitWidth();
                            setZoomMenuOpen(false);
                          }}
                        >
                          Fit width
                        </ZoomItem>
                        <ZoomItem
                          onClick={() => {
                            handleFitPage();
                            setZoomMenuOpen(false);
                          }}
                        >
                          Fit page
                        </ZoomItem>
                        {ZOOM_PRESETS.map((z) => (
                          <ZoomItem
                            key={z}
                            $active={Math.round(zoom) === z}
                            onClick={() => {
                              handleSetZoom(z);
                              setZoomMenuOpen(false);
                            }}
                          >
                            {z}%
                          </ZoomItem>
                        ))}
                      </ZoomDropdown>
                    )}
                  </ZoomWrap>
                  <ZoomBtn
                    onClick={handleZoomIn}
                    disabled={zoom >= 220}
                    title="Zoom in"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      <line x1="11" y1="8" x2="11" y2="14" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </ZoomBtn>
                </ZoomGroup>
              )}
            </ToolLeft>

            <DocTitleArea>
              <DocTitle title={doc.name}>
                {doc.name.length > 48 ? doc.name.slice(0, 48) + "…" : doc.name}
              </DocTitle>
              <DocSubtitle>
                {ft.toUpperCase()}
                {numPages ? ` · ${numPages} pages` : ""}
              </DocSubtitle>
            </DocTitleArea>

            <ToolRight>
              {isPdf &&
                (activeSection === "trial" ||
                  activeSection === "private" ||
                  doc.bundle) && (
                  <>
                    <CompareBtn
                      $active={compareOpen}
                      onClick={() => {
                        if (compareOpen) {
                          setCompareDoc(null);
                          setCompareOpen(false);
                        } else {
                          setShowSelectCompareModal(true);
                          if (coreCompareDocs.length === 0) {
                            fetchAllCoreDocs();
                          }
                        }
                      }}
                    >
                      {compareOpen ? "Close Split" : "Split View"}
                    </CompareBtn>
                    <ToolDivider />
                  </>
                )}
              {/* <NoteModeToggle
                $active={noteMode}
                onClick={() => setNoteModeState((o) => !o)}
              >
                {noteMode ? "Exit Note Mode" : "Add Note"}
              </NoteModeToggle> */}
              <ToolDivider />
              <DlWrap ref={dlRef}>
                <DownloadTrigger onClick={() => setDlOpen((o) => !o)}>
                  Download
                </DownloadTrigger>
                <DownloadMenu
                  open={dlOpen}
                  doc={activeDocObj}
                  annotations={activeAnnotations}
                  notes={activeNotes}
                  isPdf={activeFt === "pdf"}
                  onClose={() => setDlOpen(false)}
                />
              </DlWrap>
              <ToolDivider />
              <IconBtn
                onClick={toggleFullscreen}
                $active={isFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                    <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                    <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                    <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                  </svg>
                ) : (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                    <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                    <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                    <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                  </svg>
                )}
              </IconBtn>
              <PanelToggle
                $active={rightOpen}
                onClick={() => setRightOpen((o) => !o)}
                title="Toggle panel"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="15" y1="3" x2="15" y2="21" />
                </svg>
                {(activeAnnotations.length > 0 || activeNotes.length > 0) && (
                  <PanelBadge>
                    {activeAnnotations.length + activeNotes.length}
                  </PanelBadge>
                )}
              </PanelToggle>
            </ToolRight>
          </Toolbar>

          {noteMode && (
            <NoteBar>
              <span>Click anywhere on the document to place a note.</span>
              <EscKey onClick={() => setNoteModeState(false)}>
                Esc to exit
              </EscKey>
            </NoteBar>
          )}
          {isFullscreen && (
            <FullBar>
              Press <strong>Esc</strong> to exit fullscreen
            </FullBar>
          )}

          {/* ── Canvas / scroll area ── */}
          {compareOpen && compareDoc ? (
            <CompareSplitLayout>
              <SplitPane onClick={() => setActiveCompareSide("original")}>
                <SplitPaneHeader
                  style={{
                    background:
                      activeCompareSide === "original" ? "#f0fdf4" : undefined,
                  }}
                >
                  <SplitPaneTitle title={doc.name}>
                    <strong>Original:</strong>{" "}
                    {doc.name.length > 32
                      ? doc.name.slice(0, 32) + "…"
                      : doc.name}
                  </SplitPaneTitle>
                  <SplitPaneRight>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <svg
                        onClick={() => goToPage(currentPage - 1)}
                        style={{
                          cursor: currentPage <= 1 ? "default" : "pointer",
                          opacity: currentPage <= 1 ? 0.3 : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      <PageInput
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={pageInputValue}
                        onChange={handlePageInputChange}
                        onFocus={(e) => e.currentTarget.select()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            commitPageInput();
                            const target = e.currentTarget;
                            setTimeout(() => {
                              if (target) {
                                target.focus();
                                target.select();
                              }
                            }, 0);
                          }
                        }}
                        onBlur={commitPageInput}
                        style={{
                          width: "32px",
                          padding: "2px 0",
                          height: "22px",
                          fontSize: "11px",
                        }}
                      />
                      <PageTotal
                        style={{ fontSize: "11px", color: G.slate500 }}
                      >
                        / {numPages || "—"}
                      </PageTotal>
                      <svg
                        onClick={() => goToPage(currentPage + 1)}
                        style={{
                          cursor:
                            !numPages || currentPage >= numPages
                              ? "default"
                              : "pointer",
                          opacity:
                            !numPages || currentPage >= numPages ? 0.3 : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginLeft: 4,
                      }}
                    >
                      <SplitZoomBtn
                        onClick={() => setZoom((z) => Math.max(30, z - 10))}
                        title="Zoom out"
                      >
                        -
                      </SplitZoomBtn>
                      <span
                        style={{
                          fontSize: 11,
                          color: G.slate500,
                          minWidth: 32,
                          textAlign: "center",
                        }}
                      >
                        {Math.round(zoom)}%
                      </span>
                      <SplitZoomBtn
                        onClick={() => setZoom((z) => Math.min(220, z + 10))}
                        title="Zoom in"
                      >
                        +
                      </SplitZoomBtn>
                    </div>
                    <SplitViewModeBtn onClick={togglePageView}>
                      {singlePageView ? "Scroll View" : "Page View"}
                    </SplitViewModeBtn>
                  </SplitPaneRight>
                </SplitPaneHeader>
                <SplitCanvasArea ref={canvasRef} tabIndex={-1}>
                  <PdfViewer
                    url={doc.s3url}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    setNumPages={setNumPages}
                    numPages={numPages}
                    zoom={zoom}
                    annotations={annotations}
                    notes={notes}
                    onAddAnnot={addAnnot}
                    onDeleteAnnot={deleteAnnot}
                    onAddNote={addNote}
                    onDeleteNote={deleteNote}
                    noteMode={
                      compareOpen && compareDoc
                        ? activeCompareSide === "original"
                          ? noteMode
                          : false
                        : noteMode
                    }
                    scrollContainerRef={canvasRef}
                    scrollTargetPage={scrollTargetPage}
                    onScrollTargetConsumed={() => setScrollTargetPage(null)}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    isProgrammaticScroll={isProgrammaticScrollRef}
                    singlePageView={singlePageView}
                  />
                </SplitCanvasArea>
              </SplitPane>

              <SplitPane onClick={() => setActiveCompareSide("compare")}>
                <SplitPaneHeader
                  style={{
                    background:
                      activeCompareSide === "compare" ? "#f0fdf4" : undefined,
                  }}
                >
                  <SplitPaneTitle title={compareDoc.name}>
                    <strong>Split:</strong>{" "}
                    {compareDoc.name.length > 32
                      ? compareDoc.name.slice(0, 32) + "…"
                      : compareDoc.name}
                  </SplitPaneTitle>
                  <SplitPaneRight>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <svg
                        onClick={() => goToComparePage(comparePage - 1)}
                        style={{
                          cursor: comparePage <= 1 ? "default" : "pointer",
                          opacity: comparePage <= 1 ? 0.3 : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      <PageInput
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={comparePageInputValue}
                        onChange={handleComparePageInputChange}
                        // Match the original viewer: selecting the value lets
                        // a single Backspace clear values such as "11".
                        onFocus={(e) => e.currentTarget.select()}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.currentTarget.select();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            commitComparePageInput();
                            const target = e.currentTarget;
                            setTimeout(() => {
                              if (target) {
                                target.focus();
                                target.select();
                              }
                            }, 0);
                          }
                        }}
                        onBlur={(e) => {
                          e.stopPropagation();
                          commitComparePageInput();
                        }}
                        style={{
                          width: "32px",
                          padding: "2px 0",
                          height: "22px",
                          fontSize: "11px",
                        }}
                      />
                      <PageTotal
                        style={{ fontSize: "11px", color: G.slate500 }}
                      >
                        / {compareNumPages || "—"}
                      </PageTotal>
                      <svg
                        onClick={() => goToComparePage(comparePage + 1)}
                        style={{
                          cursor:
                            !compareNumPages || comparePage >= compareNumPages
                              ? "default"
                              : "pointer",
                          opacity:
                            !compareNumPages || comparePage >= compareNumPages
                              ? 0.3
                              : 1,
                          width: 14,
                          height: 14,
                        }}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginLeft: 4,
                      }}
                    >
                      <SplitZoomBtn
                        onClick={() =>
                          setCompareZoom((z) => Math.max(30, z - 10))
                        }
                        title="Zoom out"
                      >
                        -
                      </SplitZoomBtn>
                      <span
                        style={{
                          fontSize: 11,
                          color: G.slate500,
                          minWidth: 32,
                          textAlign: "center",
                        }}
                      >
                        {Math.round(compareZoom)}%
                      </span>
                      <SplitZoomBtn
                        onClick={() =>
                          setCompareZoom((z) => Math.min(220, z + 10))
                        }
                        title="Zoom in"
                      >
                        +
                      </SplitZoomBtn>
                    </div>
                    <SplitViewModeBtn onClick={toggleComparePageView}>
                      {compareSinglePageView ? "Scroll View" : "Page View"}
                    </SplitViewModeBtn>
                    <SplitHeaderActionBtn
                      onClick={() => setShowSelectCompareModal(true)}
                    >
                      Change
                    </SplitHeaderActionBtn>
                    {/* Removed duplicate Close button from right split header */}
                  </SplitPaneRight>
                </SplitPaneHeader>
                <SplitCanvasArea ref={compareCanvasRef} tabIndex={-1}>
                  <PdfViewer
                    url={compareDoc.s3url}
                    currentPage={comparePage}
                    setCurrentPage={setComparePage}
                    setNumPages={setCompareNumPages}
                    numPages={compareNumPages}
                    zoom={compareZoom}
                    annotations={compareAnnotations}
                    notes={compareNotes}
                    onAddAnnot={addCompareAnnot}
                    onDeleteAnnot={deleteCompareAnnot}
                    onAddNote={addCompareNote}
                    onDeleteNote={deleteCompareNote}
                    noteMode={
                      compareOpen && compareDoc
                        ? activeCompareSide === "compare"
                          ? noteMode
                          : false
                        : false
                    }
                    scrollContainerRef={compareCanvasRef}
                    scrollTargetPage={compareScrollTargetPage}
                    onScrollTargetConsumed={() =>
                      setCompareScrollTargetPage(null)
                    }
                    pageSize={comparePageSize}
                    setPageSize={setComparePageSize}
                    isProgrammaticScroll={isCompareProgrammaticScrollRef}
                    singlePageView={compareSinglePageView}
                  />
                </SplitCanvasArea>
              </SplitPane>
            </CompareSplitLayout>
          ) : (
            <CanvasArea ref={canvasRef} tabIndex={-1}>
              {isPdf && (
                <PdfViewer
                  url={doc.s3url}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  setNumPages={setNumPages}
                  numPages={numPages}
                  zoom={zoom}
                  annotations={annotations}
                  notes={notes}
                  onAddAnnot={addAnnot}
                  onDeleteAnnot={deleteAnnot}
                  onAddNote={addNote}
                  onDeleteNote={deleteNote}
                  noteMode={noteMode}
                  scrollContainerRef={canvasRef}
                  scrollTargetPage={scrollTargetPage}
                  onScrollTargetConsumed={() => setScrollTargetPage(null)}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  isProgrammaticScroll={isProgrammaticScrollRef}
                  singlePageView={singlePageView}
                />
              )}
              {isDocx && (
                <DocxViewer
                  url={doc.s3url}
                  annotations={annotations}
                  notes={notes}
                  onAddAnnot={addAnnot}
                  onDeleteAnnot={deleteAnnot}
                  onAddNote={addNote}
                  onDeleteNote={deleteNote}
                  noteMode={noteMode}
                />
              )}
              {isImage && <ImageViewer url={doc.s3url} name={doc.name} />}
              {isTxt && <TxtViewer url={doc.s3url} />}
              {isVideo && <VideoViewer url={doc.s3url} name={doc.name} />}
              {isAudio && <AudioViewer url={doc.s3url} name={doc.name} />}
              {(isOffice || isLegacyDoc) && <OfficeViewer url={doc.s3url} />}
              {!isPdf &&
                !isDocx &&
                !isImage &&
                !isTxt &&
                !isOffice &&
                !isLegacyDoc &&
                !isVideo &&
                !isAudio && (
                  <ErrorBox>
                    Preview not available for this file type ({ft || "unknown"}
                    ).
                    <br />
                    <a
                      href={doc.s3url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open / Download
                    </a>
                  </ErrorBox>
                )}
            </CanvasArea>
          )}
        </MainArea>

        {/* ── Right Panel ── */}
        {rightOpen && (
          <RightPanel>
            <RightPanelTopBar>
              <RightPanelHeading>Document Panel</RightPanelHeading>
              <ClosePanel onClick={() => setRightOpen(false)}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </ClosePanel>
            </RightPanelTopBar>
            <RightSidePanel
              annotations={activeAnnotations}
              notes={activeNotes}
              onDeleteAnnot={activeDeleteAnnot}
              onDeleteNote={activeDeleteNote}
              visibility={activeDocObj?.visibilityType || "me"}
              onChangeVisibility={changeVisibility}
              doc={activeDocObj}
              numPages={activeNumPages}
              ft={activeFt}
              hideVisibility={compareOpen && compareDoc}
            />
          </RightPanel>
        )}
      </ViewerBody>

      {/* ── Select Document to Compare Modal ── */}
      {showSelectCompareModal && (
        <ModalBackdrop onClick={() => setShowSelectCompareModal(false)}>
          <ModalCard
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600 }}
          >
            <ModalHeader>
              <ModalAccent style={{ background: "#00838c" }} />
              <ModalHeaderContent>
                <ModalTitle>Select Document to Split</ModalTitle>
                <ModalSubtitle>
                  Choose a PDF document from either the Core Bundle or Trial
                  Bundles to Split side-by-side.
                </ModalSubtitle>
              </ModalHeaderContent>
              <ModalCloseBtn onClick={() => setShowSelectCompareModal(false)}>
                <IcoX size={14} color={G.slate500} />
              </ModalCloseBtn>
            </ModalHeader>
            <ModalBody style={{ padding: "16px 24px" }}>
              <FieldGroup style={{ marginBottom: 15 }}>
                <FieldInput
                  placeholder="Search by Document Name or Tab/Ref..."
                  value={compareSearchQuery}
                  onChange={(e) => setCompareSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${G.slate300}`,
                    borderRadius: 4,
                  }}
                  autoFocus
                />
              </FieldGroup>

              {loadingCompareDocs ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px 0",
                    gap: 10,
                  }}
                >
                  <Spinner $size="24px" />
                  <span style={{ fontSize: 13, color: G.slate500 }}>
                    Loading documents...
                  </span>
                </div>
              ) : (
                <CompareDocList>
                  {/* Core Bundle Section */}
                  {filteredCoreDocs.length > 0 && (
                    <>
                      <CompareDocSectionHeader>
                        Core Bundle Documents
                      </CompareDocSectionHeader>
                      {filteredCoreDocs.map((item) => (
                        <CompareDocItem
                          key={item.id}
                          $active={compareDoc?.s3url === item.s3url}
                          onClick={() => {
                            setCompareDoc(item);
                            setComparePage(1);
                            setCompareNumPages(null);
                            setCompareZoom(90);
                            setCompareScrollTargetPage(null);
                            setCompareSinglePageView(false);
                            setCompareAnnotations(loadAnnotations(item.id));
                            setCompareNotes(loadNotes(item.id));
                            setShowSelectCompareModal(false);
                            setCompareOpen(true);
                            setActiveCompareSide("compare");
                          }}
                        >
                          <CompareDocIcon
                            style={{ background: "#eff6ff", color: "#3b82f6" }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                              <polyline points="13 2 13 9 20 9" />
                            </svg>
                          </CompareDocIcon>
                          <CompareDocInfo>
                            <CompareDocName title={item.name}>
                              {item.name}
                            </CompareDocName>
                            <CompareDocMeta
                              style={{ flexWrap: "wrap", rowGap: "4px" }}
                            >
                              <DocMetaBadge
                                $bg={getOwnerBadgeStyle(item.ownerType).bg}
                                $color={
                                  getOwnerBadgeStyle(item.ownerType).color
                                }
                              >
                                {getOwnerRoleLabel(item.ownerType)}
                              </DocMetaBadge>
                              <DocMetaBadge
                                $bg={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .bg
                                }
                                $color={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .color
                                }
                              >
                                {
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .label
                                }
                              </DocMetaBadge>
                            </CompareDocMeta>
                          </CompareDocInfo>
                          {item.pageCount ? (
                            <div
                              style={{
                                fontSize: 11,
                                color: G.slate500,
                                marginLeft: "auto",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                paddingLeft: 12,
                              }}
                            >
                              {item.pageCount} page
                              {item.pageCount > 1 ? "s" : ""}
                            </div>
                          ) : null}
                        </CompareDocItem>
                      ))}
                    </>
                  )}

                  {/* Trial Bundle Section */}
                  {filteredTrialDocs.length > 0 && (
                    <>
                      <CompareDocSectionHeader>
                        Trial Bundle Documents
                      </CompareDocSectionHeader>
                      {filteredTrialDocs.map((item) => (
                        <CompareDocItem
                          key={item.id}
                          $active={compareDoc?.s3url === item.s3url}
                          onClick={() => {
                            setCompareDoc(item);
                            setComparePage(1);
                            setCompareNumPages(null);
                            setCompareZoom(90);
                            setCompareScrollTargetPage(null);
                            setCompareSinglePageView(false);
                            setCompareAnnotations(loadAnnotations(item.id));
                            setCompareNotes(loadNotes(item.id));
                            setShowSelectCompareModal(false);
                            setCompareOpen(true);
                            setActiveCompareSide("compare");
                          }}
                        >
                          <CompareDocIcon
                            style={{ background: "#fdf4ff", color: "#d946ef" }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <polygon points="12 2 20 7 20 17 12 22 4 17 4 7 12 2" />
                              <polyline points="12 12 20 7" />
                              <polyline points="12 12 12 22" />
                              <polyline points="12 12 4 7" />
                            </svg>
                          </CompareDocIcon>
                          <CompareDocInfo>
                            <CompareDocName title={item.name}>
                              {item.name}
                            </CompareDocName>
                            <CompareDocMeta
                              style={{ flexWrap: "wrap", rowGap: "4px" }}
                            >
                              <DocMetaBadge $bg="#f5f3ff" $color="#5b21b6">
                                Bundle: {item.bundleName}
                              </DocMetaBadge>
                              <DocMetaBadge $bg="#f0fdfa" $color="#0f766e">
                                Folder: {item.folderName}
                              </DocMetaBadge>
                              <DocMetaBadge $bg="#fef3c7" $color="#b45309">
                                Tab/Ref:{" "}
                                {item.tabRef ||
                                  (item.bundleCode &&
                                  item.tabNum != null &&
                                  item.tabNum !== ""
                                    ? `${item.bundleCode}/${item.tabNum}`
                                    : item.tabNum)}
                              </DocMetaBadge>
                              <DocMetaBadge
                                $bg={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .bg
                                }
                                $color={
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .color
                                }
                              >
                                {
                                  getVisibilityBadgeStyle(item.visibilityType)
                                    .label
                                }
                              </DocMetaBadge>
                            </CompareDocMeta>
                          </CompareDocInfo>
                          {item.pageCount ? (
                            <div
                              style={{
                                fontSize: 11,
                                color: G.slate500,
                                marginLeft: "auto",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                paddingLeft: 12,
                              }}
                            >
                              {item.pageCount} page
                              {item.pageCount > 1 ? "s" : ""}
                            </div>
                          ) : null}
                        </CompareDocItem>
                      ))}
                    </>
                  )}

                  {filteredCoreDocs.length === 0 &&
                    filteredTrialDocs.length === 0 && (
                      <CompareDocEmpty>
                        No PDF documents found matching your search.
                      </CompareDocEmpty>
                    )}
                </CompareDocList>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={() => setShowSelectCompareModal(false)}>
                Close
              </ModalCancelBtn>
            </ModalFooter>
          </ModalCard>
        </ModalBackdrop>
      )}
    </AppWrapper>
  );
}

// ─── Compare View Styled Components ───────────────────────────────────────────

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: ${(props) => props.$size || "20px"};
  height: ${(props) => props.$size || "20px"};
  border: 2px solid ${G.slate200};
  border-top-color: ${G.teal};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const CompareBtn = styled.button`
  background: ${(props) => (props.$active ? G.teal : "transparent")};
  color: ${(props) => (props.$active ? "#ffffff" : G.teal)};
  border: 1px solid ${G.teal};
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:hover {
    background: ${(props) => (props.$active ? G.tealDark : G.tealGhost)};
  }
`;

const CompareDocList = styled.div`
  max-height: 350px;
  overflow-y: auto;
  border: 1px solid ${G.slate200};
  border-radius: 6px;
  margin-top: 10px;
  background: ${G.slate50};
`;

const CompareDocSectionHeader = styled.div`
  background: ${G.slate100};
  padding: 8px 12px;
  font-weight: 600;
  font-size: 11px;
  color: ${G.slate600};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-bottom: 1px solid ${G.slate200};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const CompareDocItem = styled.div`
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border-bottom: 1px solid ${G.slate100};
  transition: all 0.15s ease-in-out;
  background: ${(props) => (props.$active ? G.tealLight : "white")};

  &:hover {
    background: ${(props) => (props.$active ? G.tealLight : G.slate50)};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CompareDocIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 4px;
  background: ${G.tealLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${G.teal};
  flex-shrink: 0;
`;

const CompareDocInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const CompareDocName = styled.div`
  font-size: 13px;
  color: #293461;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompareDocMeta = styled.div`
  font-size: 11px;
  color: ${G.slate500};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DocMetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  background: ${(props) => props.$bg || G.slate100};
  color: ${(props) => props.$color || G.slate600};
  font-size: 10px;
  font-weight: 600;
  border-radius: 4px;
  text-transform: capitalize;
  font-family: inherit;
`;

const CompareDocEmpty = styled.div`
  padding: 20px;
  text-align: center;
  color: ${G.slate400};
  font-size: 13px;
`;

const CompareSplitLayout = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  overflow: hidden;
  background: #f1f5f9;
  min-height: 0;
`;

const SplitPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #ffffff;
  min-width: 0;

  &:first-child {
    border-right: 2px solid ${G.slate200};
  }
`;

const SplitPaneHeader = styled.div`
  background: ${G.slate50};
  border-bottom: 1px solid ${G.slate200};
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const SplitPaneTitle = styled.div`
  font-size: 12px;
  color: #293461;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 45%;
`;

const SplitPaneRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SplitZoomBtn = styled.button`
  background: transparent;
  border: 1px solid ${G.slate300};
  border-radius: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  color: ${G.slate600};
  transition: all 0.15s;

  &:hover {
    background: ${G.slate100};
    border-color: ${G.slate400};
  }
`;

const SplitHeaderActionBtn = styled.button`
  background: ${(props) => (props.$danger ? "transparent" : G.teal)};
  color: ${(props) => (props.$danger ? G.red : "#ffffff")};
  border: 1px solid ${(props) => (props.$danger ? G.red : G.teal)};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) => (props.$danger ? G.redLight : G.tealDark)};
    color: ${(props) => (props.$danger ? G.redDark : "#ffffff")};
  }
`;

const SplitCanvasArea = styled.div`
  flex: 1;
  background: #f1f5f9;
  overflow-y: auto;
  outline: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
  scroll-behavior: auto;
  width: 100%;
  height: 100%;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const shimmerMove = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

const PlaceholderWrap = styled.div`
  background: #ffffff;
  border-radius: 3px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

const PlaceholderInner = styled.div`
  padding: 48px 52px;
`;

const SkeletonShimmer = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.7) 50%,
    transparent 100%
  );
  animation: ${shimmerMove} 1.6s ease infinite;
  z-index: 1;
`;

const DocxSkeletonWrap = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 840px;
  border-radius: 3px;
  position: relative;
  overflow: hidden;
`;

const DocxSkeletonInner = styled.div`
  padding: 60px 72px;
`;

const SkeletonBar = styled.div`
  background: #e8edf3;
  border-radius: 4px;
`;

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #e9eef6;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #1e293b;
  overflow: hidden;
  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      background: #0f172a;
    `}
`;

const TabBar = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #dde6f0;
  height: 48px;
  flex-shrink: 0;
  padding: 0 14px;
  box-shadow: 0 1px 8px rgba(15, 23, 42, 0.06);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  padding: 5px 10px;
  border-radius: 7px;
  white-space: nowrap;
  flex-shrink: 0;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const TabDivider = styled.div`
  width: 1px;
  height: 20px;
  background: #e2e8f0;
  flex-shrink: 0;
  margin: 0 10px;
`;

const TabScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  overflow-x: auto;
  flex: 1;
  &::-webkit-scrollbar {
    height: 0;
  }
`;

const DocTab = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#ecfeff" : "transparent")};
  border: 1px solid ${({ $active }) => ($active ? "#a5f3fc" : "transparent")};
  font-size: 12px;
  color: ${({ $active }) => ($active ? "#0f172a" : "#64748b")};
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 220px;
  transition: all 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

const TabFilePill = styled.span`
  font-size: 9px;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 4px;
  flex-shrink: 0;
  background: ${({ $ft }) => ($ft === "pdf" ? "#fee2e2" : "#dbeafe")};
  color: ${({ $ft }) => ($ft === "pdf" ? "#dc2626" : "#2563eb")};
`;

const TabName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
`;

const TabCloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #cbd5e1;
  padding: 2px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
`;

const ViewerBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  position: relative;
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #dfe8f2;
  padding: 9px 16px;
  gap: 12px;
  flex-shrink: 0;
  min-height: 56px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
  z-index: 20;
`;

const ToolLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ToolRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
`;

const ToolDivider = styled.div`
  width: 1px;
  height: 20px;
  background: #e8edf3;
  flex-shrink: 0;
  margin: 0 2px;
`;

const PageNav = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  background: #f8fbfd;
  border: 1px solid #dbe5ef;
  border-radius: 9px;
  padding: 3px 5px;
`;

const ViewModeBtn = styled.button`
  height: 28px;
  padding: 0 9px;
  border: 1px solid #dbe5ef;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  &:hover {
    background: #e6f7f8;
    border-color: #00818a;
    color: #00717a;
  }
`;

const PageBtn = styled.button`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  border-radius: 5px;
  transition:
    background 0.15s,
    color 0.15s;
  &:hover:not(:disabled) {
    background: #e6f7f8;
    color: #00818a;
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const PageInput = styled.input`
  width: 36px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  outline: none;
  color: #1e293b;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-align: center;
  padding: 3px 0;
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }
  &:focus {
    border-color: #00818a;
    box-shadow: 0 0 0 2px rgba(0, 129, 138, 0.12);
  }
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
`;

const PageTotal = styled.span`
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
  padding: 0 2px;
`;

const ZoomGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f8fbfd;
  border: 1px solid #dbe5ef;
  border-radius: 10px;
  padding: 3px;
`;

const ZoomBtn = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: #ffffff;
    color: #00818a;
    border-color: #c8edf0;
    box-shadow: 0 2px 7px rgba(15, 23, 42, 0.08);
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const ZoomWrap = styled.div`
  position: relative;
`;

const ZoomLabel = styled.button`
  background: #ffffff;
  border: 1px solid #dbe5ef;
  border-radius: 8px;
  color: #1e293b;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  padding: 4px 10px;
  min-width: 58px;
  height: 28px;
  text-align: center;
  transition: all 0.15s;
  &:hover {
    background: #ecfeff;
    border-color: #67dce3;
    color: #006a72;
  }
`;

const ZoomDropdown = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
  min-width: 160px;
  z-index: 600;
  overflow: hidden;
  animation: ${fadeUp} 0.15s ease;
`;

const ZoomItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 9px 14px;
  font-size: 12.5px;
  border: none;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontRegular};
  transition: background 0.12s;
  background: ${({ $active }) => ($active ? "#f0fafa" : "transparent")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const DocTitleArea = styled.div`
  flex: 1;
  text-align: center;
  min-width: 0;
  padding: 0 12px;
`;

const DocTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocSubtitle = styled.div`
  font-size: 10px;
  color: #94a3b8;
  margin-top: 1px;
`;
/*
const NoteModeToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1.5px solid ${({ $active }) => ($active ? "#f59e0b" : "#e8edf3")};
  border-radius: 7px;
  padding: 5px 11px;
  font-size: 11.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#fffbeb" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#d97706" : "#64748b")};
  transition: all 0.15s;
  white-space: nowrap;
  &:hover {
    background: #fffbeb;
    border-color: #f59e0b;
    color: #d97706;
  }
`;
*/
const DlWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const DownloadTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #00818a, #006a72);
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 6px 13px;
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 129, 138, 0.22);
  transition: all 0.15s;
  &:hover {
    background: linear-gradient(135deg, #006a72, #00575e);
    box-shadow: 0 4px 12px rgba(0, 129, 138, 0.32);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
`;

const DlDropdown = styled.div`
  position: absolute;
  top: calc(100% + 7px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.13);
  min-width: 260px;
  z-index: 600;
  overflow: hidden;
  animation: ${fadeUp} 0.15s ease;
`;

const DlItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  font-family: ${theme.fonts.primaryFontRegular};
  padding: 13px 16px;
  font-size: 12.5px;
  border: none;
  background: none;
  cursor: pointer;
  color: #475569;
  transition: background 0.12s;
  &:hover {
    background: #f8fafc;
    color: #1e293b;
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
  & + & {
    border-top: 1px solid #f1f5f9;
  }
`;

const DlItemTitle = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
`;

const DlItemSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`;

const IconBtn = styled.button`
  width: 32px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${({ $active }) => ($active ? "#00818a" : "#e8edf3")};
  border-radius: 7px;
  background: ${({ $active }) => ($active ? "#f0fafa" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
    border-color: #c8d3df;
  }
`;

const PanelToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1.5px solid ${({ $active }) => ($active ? "#00818a" : "#e8edf3")};
  border-radius: 7px;
  padding: 5px 9px;
  background: ${({ $active }) => ($active ? "#f0fafa" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#00818a" : "#64748b")};
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
  &:hover {
    background: #f0fafa;
    border-color: #00818a55;
    color: #00818a;
  }
`;

const PanelBadge = styled.span`
  background: #00818a;
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 5px;
  min-width: 16px;
  text-align: center;
`;

const NoteBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fffbeb;
  border-bottom: 1px solid #fde68a;
  padding: 7px 18px;
  font-size: 12px;
  color: #92400e;
  flex-shrink: 0;
  animation: ${fadeUp} 0.18s ease;
`;

const EscKey = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 5px;
  padding: 2px 9px;
  font-size: 11px;
  cursor: pointer;
  color: #92400e;
  font-family: ${theme.fonts.primaryFontSemiBold};
  &:hover {
    background: #fde68a;
  }
`;

const FullBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.8);
  padding: 5px;
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
  strong {
    color: #e2e8f0;
  }
`;

const CanvasArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  background:
    radial-gradient(
      circle at top left,
      rgba(0, 129, 138, 0.08),
      transparent 28%
    ),
    linear-gradient(180deg, #e6edf6 0%, #d7e1ed 100%);
  padding: 12px 20px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #b4c0d0;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  &:focus {
    outline: none;
  }
  @media (max-width: 640px) {
    padding: 10px 8px 10px;
  }
`;

const PdfWrap = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  user-select: ${({ $noteMode }) => ($noteMode ? "none" : "text")};
  cursor: ${({ $noteMode }) => ($noteMode ? "crosshair" : "text")};
  position: relative;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 20px 48px rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
`;

const PdfList = styled.div`
  position: relative;
  margin: 0 auto;
`;

const PdfPageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 22px;
  background: #fff;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12);
  border-radius: 3px;
  overflow: hidden;
  content-visibility: auto;
  contain-intrinsic-size: 800px 1100px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const DocxScrollArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DocxContent = styled.div`
  background: #fff;
  width: 100%;
  max-width: 840px;
  padding: 64px 72px;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 20px 48px rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  animation: ${fadeUp} 0.3s ease;
  line-height: 1.9;
  font-size: 14.5px;
  color: #1a1a2e;
  user-select: ${({ $noteMode }) => ($noteMode ? "none" : "text")};
  cursor: ${({ $noteMode }) => ($noteMode ? "crosshair" : "text")};
  position: relative;
  letter-spacing: 0.01em;
  @media (max-width: 640px) {
    padding: 32px 24px;
    font-size: 13px;
  }
  h1,
  h2,
  h3,
  h4 {
    color: #1a237e;
    margin: 1.4em 0 0.6em;
  }
  h1 {
    font-size: 1.55em;
    font-weight: 700;
    border-bottom: 2px solid #e8edf5;
    padding-bottom: 0.3em;
  }
  h2 {
    font-size: 1.25em;
    font-weight: 600;
  }
  p {
    margin-bottom: 1em;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1.2em 0;
  }
  th,
  td {
    border: 1px solid #dde3ec;
    padding: 9px 13px;
    text-align: left;
  }
  th {
    background: #f5f7fb;
    font-weight: 600;
    color: #374151;
  }
  tr:hover td {
    background: #fafbfd;
  }
  ul,
  ol {
    padding-left: 1.8em;
    margin-bottom: 1em;
  }
  li {
    margin-bottom: 0.3em;
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
  }
  blockquote {
    border-left: 4px solid #00818a;
    margin: 1.2em 0;
    padding: 0.8em 1.2em;
    background: #f0fafa;
    border-radius: 0 6px 6px 0;
    color: #374151;
  }
  ::selection {
    background: rgba(0, 129, 138, 0.15);
  }
  mark:hover {
    filter: brightness(0.9);
  }
`;

const RightPanel = styled.aside`
  position: absolute;
  top: 56px;
  bottom: 0;
  right: 0;
  z-index: 30;
  width: 320px;
  background: #ffffff;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: -4px 0 16px rgba(15, 23, 42, 0.08);
  animation: ${slideIn} 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  @media (max-width: 900px) {
    display: none;
  }
`;

const RightPanelTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #e8edf3;
  flex-shrink: 0;
  background: #fafbfc;
`;

const RightPanelHeading = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: 0.3px;
`;

const ClosePanel = styled.button`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  border-radius: 5px;
  transition:
    background 0.15s,
    color 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

const RightPanelScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  padding: 0 0 16px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const SectionBlock = styled.div`
  padding: 18px 16px;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: #f0f4f8;
  margin: 0 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const SectionBadge = styled.span`
  background: ${({ $color }) => $color + "18"};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color + "30"};
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  min-width: 22px;
  text-align: center;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 12px 8px;
  gap: 6px;
`;

const EmptyTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AnnotCard = styled.div`
  display: flex;
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  overflow: hidden;
  animation: ${fadeUp} 0.2s ease;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  &:hover {
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const AnnotColorStrip = styled.div`
  width: 4px;
  flex-shrink: 0;
`;

const AnnotCardBody = styled.div`
  flex: 1;
  padding: 10px 11px;
  min-width: 0;
`;

const AnnotCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 5px;
`;

const AnnotSnippet = styled.div`
  font-size: 11.5px;
  color: #475569;
  font-style: italic;
  line-height: 1.45;
  flex: 1;
  min-width: 0;
`;

const AnnotComment = styled.div`
  font-size: 11px;
  color: #059669;
  background: #f0fdf4;
  border-radius: 5px;
  padding: 4px 7px;
  margin-bottom: 6px;
  line-height: 1.45;
`;

const AnnotFooter = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const FooterChip = styled.span`
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 4px;
  font-weight: 500;
  background: ${({ $color, $yellow }) =>
    $yellow ? "#fef3c7" : $color ? $color + "20" : "#f1f5f9"};
  color: ${({ $color, $yellow }) =>
    $yellow ? "#92400e" : $color || "#64748b"};
`;

const FooterTime = styled.span`
  font-size: 10px;
  color: #94a3b8;
  margin-left: auto;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #cbd5e1;
  padding: 3px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition:
    color 0.15s,
    background 0.15s;
  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
`;

const NoteCard = styled.div`
  background: linear-gradient(140deg, #fffdf0, #fef9c3);
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 11px 12px;
  animation: ${fadeUp} 0.2s ease;
  transition:
    box-shadow 0.15s,
    transform 0.15s;
  &:hover {
    box-shadow: 0 3px 12px rgba(245, 158, 11, 0.14);
    transform: translateY(-1px);
  }
`;

const NoteCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
`;

const NoteCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const NoteCardText = styled.div`
  font-size: 12px;
  color: #713f12;
  line-height: 1.6;
`;

const InfoList = styled.div`
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
  transition: background 0.12s;
  &:hover {
    background: #fafbfc;
  }
`;

const InfoKey = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  min-width: 80px;
  flex-shrink: 0;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const InfoVal = styled.div`
  font-size: 12px;
  color: #475569;
  word-break: break-all;
  flex: 1;
`;

const FormatPill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 800;
  background: ${({ $ft }) => ($ft === "pdf" ? "#fee2e2" : "#dbeafe")};
  color: ${({ $ft }) => ($ft === "pdf" ? "#dc2626" : "#2563eb")};
`;

const StatBadge = styled.span`
  display: inline-block;
  background: ${({ $color }) => $color + "18"};
  color: ${({ $color }) => $color};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  min-width: 26px;
  text-align: center;
`;

const TipBox = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
  min-width: 270px;
  animation: ${fadeUp} 0.15s ease;
`;

const TipLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  margin-bottom: 9px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const TipPreview = styled.div`
  font-size: 11px;
  color: #475569;
  margin-bottom: 12px;
  font-style: italic;
  line-height: 1.45;
  border-left: 3px solid #f59e0b;
  padding-left: 9px;
`;

const TipColorRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const TipColorDot = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  background: ${({ $bg }) => $bg};
  border: 2.5px solid
    ${({ $selected }) => ($selected ? "#1e293b" : "transparent")};
  box-shadow: ${({ $selected }) => ($selected ? "0 0 0 2px #cbd5e1" : "none")};
  transition: transform 0.15s;
  &:hover {
    transform: scale(1.2);
  }
`;

const TipInput = styled.input`
  width: 100%;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 12px;
  color: #1e293b;
  margin-bottom: 11px;
  font-family: ${theme.fonts.primaryFontRegular};
  box-sizing: border-box;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: none;
    border-color: #00818a;
    box-shadow: 0 0 0 3px rgba(0, 129, 138, 0.1);
  }
`;

const NoteInputTA = styled.textarea`
  width: 100%;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  padding: 9px 10px;
  font-size: 12px;
  color: #1e293b;
  font-family: ${theme.fonts.primaryFontRegular};
  resize: vertical;
  box-sizing: border-box;
  margin-bottom: 7px;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const TipActions = styled.div`
  display: flex;
  gap: 7px;
  justify-content: flex-end;
`;

const TipSave = styled.button`
  background: linear-gradient(135deg, #00818a, #006a72);
  color: #fff;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border: none;
  border-radius: 7px;
  padding: 7px 14px;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 129, 138, 0.2);
  transition: all 0.15s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 129, 138, 0.3);
    transform: translateY(-1px);
  }
`;

const TipCancel = styled.button`
  background: transparent;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
  padding: 7px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

const NotePin = styled.button`
  background: #fffbeb;
  border: 1.5px solid #f59e0b;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
  padding: 0;
  transition:
    transform 0.15s,
    box-shadow 0.15s;
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 4px 14px rgba(245, 158, 11, 0.32);
  }
`;

const StickyCard = styled.div`
  position: absolute;
  left: 32px;
  top: 0;
  background: linear-gradient(135deg, #fefce8, #fef9c3);
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  padding: 11px 13px;
  min-width: 190px;
  max-width: 240px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  z-index: 200;
  animation: ${fadeUp} 0.15s ease;
`;

const StickyText = styled.div`
  font-size: 12px;
  color: #713f12;
  line-height: 1.5;
  margin-bottom: 7px;
`;
const StickyMeta = styled.div`
  font-size: 10px;
  color: #a16207;
  margin-bottom: 7px;
`;
const StickyDelete = styled.button`
  background: #fee2e2;
  color: #ef4444;
  border: 1px solid #fecaca;
  border-radius: 5px;
  padding: 3px 9px;
  font-size: 11px;
  cursor: pointer;
  &:hover {
    background: #fecaca;
  }
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 24px 32px;
  font-size: 13px;
  max-width: 480px;
  text-align: center;
  line-height: 1.7;
  margin: 40px auto;
  a {
    color: #2563eb;
  }
`;

const TruncatedText = styled.div`
  color: #475569;
  font-size: 14px;
  line-height: 1.7;
  text-align: justify;

  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $expanded }) => ($expanded ? "unset" : 4)};
  overflow: hidden;

  margin-bottom: 8px;
`;

const ExpandLink = styled.button`
  background: none;
  border: none;
  color: #00818a;
  font-size: 11px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
  display: block;
  &:hover {
    color: #006a72;
    text-decoration: none;
  }
`;

const ImageViewerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const MediaViewerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
`;

const VideoCard = styled.div`
  background: #0f172a;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  border: 1px solid #1e293b;
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MediaTitle = styled.div`
  color: #f1f5f9;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AudioCard = styled.div`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
`;

const pulse = keyframes`
  0% { transform: scale(0.96); opacity: 0.8; }
  50% { transform: scale(1.04); opacity: 1; box-shadow: 0 8px 24px rgba(0, 129, 138, 0.25); }
  100% { transform: scale(0.96); opacity: 0.8; }
`;

const AudioDisc = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00818a, #006a72);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 129, 138, 0.15);
  animation: ${pulse} 3s ease-in-out infinite;
`;

const AudioTitle = styled.div`
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TxtContent = styled.div`
  background: #fff;
  width: 100%;
  max-width: 840px;
  padding: 48px 56px;
  border-radius: 3px;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 20px 48px rgba(0, 0, 0, 0.08);
  pre {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ${theme.fonts.primaryFontRegular};
    font-size: 13px;
    line-height: 1.7;
    color: #1e293b;
  }
`;

const OfficeFrameWrap = styled.div`
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.06),
    0 8px 24px rgba(0, 0, 0, 0.12);
`;

const SplitViewModeBtn = styled.button`
  height: 22px;
  padding: 0 7px;
  border: 1px solid ${G.slate300};
  border-radius: 4px;
  background: #ffffff;
  color: ${G.slate600};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${G.tealLight};
    border-color: ${G.teal};
    color: ${G.teal};
  }
`;
